// src/components/AdminPanel.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoleActions } from '../context/RoleContext';
import { apiFetch } from '../utils/auth';
import { API_URL } from '../config';
import s from './AdminPanel.module.css';
import actionsIcon from '../icons/admin_panel_actions.svg';
import { SearchInput, Input, Textarea, DateInput, Select, UploadImage, ActionsMenu, Avatar } from './UI';
import actionsMenuStyles from './UI/ActionsMenu.module.css';
import { ReactComponent as ArrowIcon } from '../icons/select_arrow.svg';
import leaguesIcon from '../icons/leagues_icon.svg';
import { ReactComponent as ChatFormBubble } from '../icons/create_chat_form.svg';
import { ReactComponent as FabBubble } from '../icons/circle.svg';
import { ReactComponent as CloseIcon } from '../icons/close_button.svg';
import { ReactComponent as ChatPlusIcon } from '../icons/create_chat_icon.svg';
import { ReactComponent as UploadChatPictureIcon } from '../icons/upload_chat_picture.svg';
import { ReactComponent as AddIconPlus } from '../icons/add_icon_plus.svg';
import ns from './News.module.css';
import AdminPolls from './AdminPolls';
import AdminRoles from './AdminRoles';

function AdminRowMenu({
  open,
  onToggle,
  items,
  wrapClassName = s.actions,
  buttonClassName = s.iconBtn,
  title = 'Действия',
}) {
  return (
    <ActionsMenu
      open={open}
      onToggle={onToggle}
      items={items}
      title={title}
      align="left"
      trigger={<img src={actionsIcon} alt="" className={s.iconDots} />}
      wrapClassName={wrapClassName}
      buttonClassName={buttonClassName}
      menuClassName={s.menu}
    />
  );
}

const ADMIN_MENU_OUTSIDE_SELECTOR = [s.actions, s.achCardMenu, actionsMenuStyles.wrap].join(', ');

function renderEmployeeAvatar(emp, resolveImageUrl, size = 24, className = s.avatar) {
  const src = (resolveImageUrl && resolveImageUrl(emp?.avatar)) || emp?.avatar || '';
  return (
    <Avatar
      src={src}
      name={emp?.name}
      alt={emp?.name || ''}
      size={size}
      className={className}
    />
  );
}

/* ======================= ПРОКРУЧИВАЕМЫЙ ТАББАР ======================= */
function ScrollTabs({ items, activeKey, onChange, className = '' }) {
  const wrapRef = useRef(null);
  const btnRefs = useRef([]);
  const rafRef = useRef(0);
  const dragRef = useRef({ active: false, startX: 0, startLeft: 0, moved: false });
  const [ind, setInd] = useState({ left: 0, width: 0 });

  const recalcNow = useCallback(() => {
    const i = Math.max(0, items.findIndex(t => t.key === activeKey));
    const btn = btnRefs.current[i];
    const wrap = wrapRef.current;
    if (!btn || !wrap) return;

    // читаем фактические координаты

    // левая граница кнопки относительно левой границы прокручиваемого контейнера
    const left = btn.offsetLeft;
    const width = btn.offsetWidth;

    setInd({ left, width });
  }, [items, activeKey]);

  // плавное обновление: не чаще 1 раза за кадр
  const recalc = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(recalcNow);
  }, [recalcNow]);

  useEffect(() => {
    recalc();
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [recalc]);

  // при прокрутке контейнера — тоже пересчитываем
  const onScroll = () => recalc();
  const onWheelTabs = (e) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;
    if (wrap.scrollWidth <= wrap.clientWidth) return;
    e.preventDefault();
    wrap.scrollLeft += e.deltaY;
  };
  const onMouseDownTabs = (e) => {
    const wrap = wrapRef.current;
    if (!wrap || e.button !== 0) return;
    dragRef.current = { active: true, startX: e.clientX, startLeft: wrap.scrollLeft, moved: false };
    wrap.classList.add(s.tabsDragging);
  };
  const onMouseMoveTabs = (e) => {
    const wrap = wrapRef.current;
    const drag = dragRef.current;
    if (!wrap || !drag.active) return;
    const dx = e.clientX - drag.startX;
    if (Math.abs(dx) > 3) drag.moved = true;
    wrap.scrollLeft = drag.startLeft - dx;
  };
  const stopMouseDragTabs = () => {
    const wrap = wrapRef.current;
    dragRef.current.active = false;
    if (wrap) wrap.classList.remove(s.tabsDragging);
  };
  const onClickCaptureTabs = (e) => {
    if (!dragRef.current.moved) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current.moved = false;
  };

  // когда активная вкладка поменялась — прокручиваем её в зону видимости и пересчитываем
  useEffect(() => {
    const i = Math.max(0, items.findIndex(t => t.key === activeKey));
    const btn = btnRefs.current[i];
    const wrap = wrapRef.current;
    if (!btn || !wrap) return;

    if (btn?.scrollIntoView) {
      btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    recalc();
    // хотим пересчитывать только при смене активного таба
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  return (
    <div
      ref={wrapRef}
      className={`${s.tabs} ${className}`}
      onScroll={onScroll}
      onWheel={onWheelTabs}
      onMouseDown={onMouseDownTabs}
      onMouseMove={onMouseMoveTabs}
      onMouseUp={stopMouseDragTabs}
      onMouseLeave={stopMouseDragTabs}
      onClickCapture={onClickCaptureTabs}
    >
      {items.map((t, i) => (
        <button
          key={t.key}
          ref={el => (btnRefs.current[i] = el)}
          className={`${s.tab} ${t.key === activeKey ? s.tabActive : ''}`}
          onClick={() => onChange(t.key)}
          type="button"
        >
          {t.label}
        </button>
      ))}
      <span
        className={s.tabIndicator}
        style={{ transform: `translateX(${ind.left}px)`, width: ind.width }}
      />
    </div>
  );
}


/* ======================= ОБЁРТКА С ТАБАМИ ======================= */
function AdminPanel() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('employees'); // 'employees' | 'birthdays' | 'achievements' | 'structure' | 'settings'

  // данные для табов
  const tabs = [
    { key: 'employees', label: 'Сотрудники' },
    { key: 'birthdays', label: 'ДР' },
    { key: 'polls', label: 'Голосования' },
    { key: 'achievements', label: 'Ачивки' },
    { key: 'structure', label: 'Структура' },
    { key: 'roles', label: 'Роли' },
    { key: 'settings', label: 'Параметры' },
    { key: 'notifications', label: 'Оповещения' },
  ];

  return (
    <div className={s.container}>
      {/* Верхняя панель: слева — таббар, справа — крестик */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, position: 'relative' }}>
        <ScrollTabs items={tabs} activeKey={tab} onChange={setTab} />
        <button
          type="button"
          className={s.closeBtn}
          onClick={() => navigate('/plus')}
          aria-label="Закрыть админку"
          title="Закрыть админку"
        >
          <CloseIcon className={s.closeIcon} />
        </button>
      </div>

      {tab === 'employees' && <AdminEmployees />}
      {tab === 'birthdays' && <AdminBirthdays />}
      {tab === 'achievements' && <AdminAchievements />}

      {tab === 'structure' && <AdminStructure />}
      {tab === 'roles' && <AdminRoles />}
      {tab === 'settings' && <AdminSettings />}
      {tab === 'polls' && <AdminPolls />}
      {tab === 'notifications' && <AdminNotifications />}
    </div>
  );
}

export default AdminPanel;

// --- общий хук выбора руководителя (должен быть на верхнем уровне файла) ---
function useManagerPicker() {
  const [search, setSearch] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [selected, setSelected] = React.useState(null); // {id, name, avatar?} | null

  React.useEffect(() => {
    const t = setTimeout(async () => {
      const q = search.trim();
      if (!q) { setResults([]); return; }
      try {
        const res = await apiFetch(`${API_URL}/employees/search/?query=${encodeURIComponent(q)}`);
        const data = res.ok ? await res.json() : [];
        const arr = Array.isArray(data) ? data : [];
        setResults(selected ? arr.filter(e => String(e.id) !== String(selected.id)) : arr);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, selected]);

  return { search, setSearch, results, setResults, selected, setSelected };
}

function useMultiEmployeePicker() {
  const [search, setSearch] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [selected, setSelected] = React.useState([]); // [{id,name,avatar?}, ...]

  React.useEffect(() => {
    const t = setTimeout(async () => {
      const q = search.trim();
      if (!q) { setResults([]); return; }

      try {
        const res = await apiFetch(`${API_URL}/employees/search/?query=${encodeURIComponent(q)}`);
        const data = res.ok ? await res.json() : [];
        const arr = Array.isArray(data) ? data : [];
        const selectedIds = new Set((selected || []).map(x => String(x.id)));
        setResults(arr.filter(e => !selectedIds.has(String(e.id))));
      } catch {
        setResults([]);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [search, selected]);

  const add = (emp) => {
    if (!emp) return;
    setSelected(prev => {
      const ids = new Set((prev || []).map(x => String(x.id)));
      if (ids.has(String(emp.id))) return prev;
      return [...(prev || []), emp];
    });
    setSearch('');
    setResults([]);
  };

  const remove = (id) => {
    setSelected(prev => (prev || []).filter(x => String(x.id) !== String(id)));
  };

  const clear = () => setSelected([]);

  return { search, setSearch, results, setResults, selected, setSelected, add, remove, clear };
}


export function SelectedManagerTag({ selected, onRemove, resolveImageUrl }) {
  if (!selected) return <div className={s.selectedTags} />;
  return (
    <div className={s.selectedTags}>
      <span className={s.tag}>
        {renderEmployeeAvatar(selected, resolveImageUrl)}
        {selected.name}
        <button
          type="button"
          className={s.tagRemove}
          onClick={onRemove}
          aria-label="Убрать руководителя"
        >
          ×
        </button>
      </span>
    </div>
  );
}

function ManagerSelector({ mgr, placeholder = "Выбор руководителя", resolveImageUrl }) {
  return (
    <>
      {mgr.selected && <SelectedManagerTag
        selected={mgr.selected}
        onRemove={() => mgr.setSelected(null)}
        resolveImageUrl={resolveImageUrl}
      />}

      {!mgr.selected && (
        <div className={s.searchContainer}>
          <SearchInput
            value={mgr.search}
            onChange={(e) => mgr.setSearch(e.target.value)}
            placeholder={placeholder}
            ariaLabel={placeholder}
          />
          {mgr.results.length > 0 && mgr.search.trim().length > 0 && (
            <ul className={s.searchDropdown}>
              {mgr.results.map((emp) => (
                <li
                  key={emp.id}
                  className={s.searchItem}
                  onClick={() => { mgr.setSelected(emp); mgr.setSearch(''); mgr.setResults([]); }}
                >
                  {renderEmployeeAvatar(emp, resolveImageUrl)}
                  {emp.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

function MultiEmployeeSelector({ picker, placeholder = "Поиск сотрудников", resolveImageUrl }) {
  return (
    <>
      {(picker.selected || []).length > 0 && <div className={s.selectedTags}>
        {(picker.selected || []).map(emp => (
          <span key={emp.id} className={s.tag}>
            {renderEmployeeAvatar(emp, resolveImageUrl)}
            {emp.name}
            <button
              type="button"
              className={s.tagRemove}
              onClick={() => picker.remove(emp.id)}
              aria-label="Убрать партнера"
            >
              ×
            </button>
          </span>
        ))}
      </div>}

      <div className={s.searchContainer}>
        <SearchInput
          value={picker.search}
          onChange={(e) => picker.setSearch(e.target.value)}
          placeholder={placeholder}
          ariaLabel={placeholder}
        />
        {picker.results.length > 0 && picker.search.trim().length > 0 && (
          <ul className={s.searchDropdown}>
            {picker.results.map(emp => (
              <li
                key={emp.id}
                className={s.searchItem}
                onClick={() => picker.add(emp)}
              >
                {renderEmployeeAvatar(emp, resolveImageUrl)}
                {emp.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function MultiSelectWithSearch({
  options,
  selectedIds,
  setSelectedIds,
  placeholder = 'Поиск...',
  getId = (item) => item.id,
  getSearchString = (item) => (item?.name ?? '').toString(),
  renderSelectedLabel,
  renderOptionLabel,
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selectedSet = new Set((selectedIds || []).map((v) => Number(v)));

  const selectedItems = (options || []).filter((opt) =>
    selectedSet.has(Number(getId(opt)))
  );
  const filtered = (options || [])
    .filter((opt) => !selectedSet.has(Number(getId(opt))))
    .filter((opt) => {
      const s = (q || '').trim().toLowerCase();
      if (!s) return true;
      return getSearchString(opt).toLowerCase().includes(s);
    });

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const add = (value) => {
    const id = Number(value);
    if (!Number.isFinite(id)) return;
    setSelectedIds([...(selectedIds || []), id]);
    setQ('');
    setOpen(false);
  };

  const remove = (value) => {
    const id = Number(value);
    setSelectedIds((selectedIds || []).filter((x) => Number(x) !== id));
  };

  const formatSelected = renderSelectedLabel || renderOptionLabel || getSearchString;
  const formatOption = renderOptionLabel || getSearchString;

  return (
    <div ref={wrapRef}>
      {selectedItems.length > 0 && (
        <div className={s.respPills}>
          {selectedItems.map((item) => {
            const id = getId(item);
            return (
              <span key={id} className={s.respPill}>
                {formatSelected(item)}
                <button
                  type="button"
                  className={s.respRemove}
                  onClick={() => remove(id)}
                  aria-label="Удалить выбранный элемент"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className={s.respInputWrap}>
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          ariaLabel={placeholder}
          className={s.respInput}
        />
        <button
          type="button"
          className={s.respChevronBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Закрыть список' : 'Открыть список'}
        >
          <ArrowIcon />
        </button>

        {open && (
          <ul className={s.searchDropdown}>
            {filtered.length === 0 ? (
              <li className={s.searchItem} style={{ opacity: 0.6, cursor: 'default' }}>
                Ничего не найдено
              </li>
            ) : (
              filtered.map((item) => {
                const id = getId(item);
                return (
                  <li
                    key={id}
                    className={s.searchItem}
                    onClick={() => add(id)}
                  >
                    {formatOption(item)}
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

function DepartmentMultiSelect({
  deps,
  selectedIds,
  setSelectedIds,
  placeholder = 'Поиск по структуре ...',
}) {
  return (
    <MultiSelectWithSearch
      options={deps}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      placeholder={placeholder}
      getId={(d) => d.id}
      getSearchString={(d) =>
        `${(d.code || '').toString()} ${(d.name || '').toString()}`
      }
      renderSelectedLabel={(d) =>
        `${d.code ? `${d.code} ` : ''}${d.name}`
      }
      renderOptionLabel={(d) =>
        `${d.code ? `${d.code} — ` : ''}${d.name}`
      }
    />
  );
}

function sortLeaguesByCode(list) {
  const arr = Array.isArray(list) ? list.slice() : [];

  arr.sort((a, b) => {
    const num = (x) => {
      const m = String(x?.code ?? '').match(/\d+/);
      return m ? parseInt(m[0], 10) : null;
    };

    const na = num(a);
    const nb = num(b);

    if (na != null && nb != null) return na - nb;

    return String(a?.name ?? '').localeCompare(
      String(b?.name ?? ''),
      'ru',
      { sensitivity: 'base' }
    );
  });

  return arr;
}


function getDefaultRoleId(roles) {
  const list = Array.isArray(roles) ? roles : [];
  return (
    list.find(r => String(r?.code || '').toLowerCase() === 'employee')?.id
    ?? list[0]?.id
    ?? null
  );
}

function FolderIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 4l2 2h8a2 2 0 012 2v10a3 3 0 01-3 3H5a3 3 0 01-3-3V6a2 2 0 012-2h6z" fill="currentColor" />
    </svg>
  );
}
function CheckIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9.2 16.2L4.9 11.9l-1.4 1.4 5.7 5.7L20.5 7.7l-1.4-1.4L9.2 16.2z" fill="currentColor" />
    </svg>
  );
}

function TrashIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 3h6l1 2h5v2H3V5h5l1-2zm1 6h2v10h-2V9zm4 0h2v10h-2V9zM7 9h2v10H7V9z" fill="currentColor" />
    </svg>
  );
}


/* ======================= СТРАНИЦА «СОТРУДНИКИ» ======================= */

function AdminEmployees() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // NEW: состояние открытия модалки создания
  const [showCreate, setShowCreate] = useState(false);
  // NEW: состояние модалки редактирования
  const [editModal, setEditModal] = useState(null); // { employee, user|null }

  // справочник рангов и лиг
  const [ranks, setRanks] = useState([]);     // { [id:number]: name }
  const [leaguesMap, setLeaguesMap] = useState({}); // { [id:number]: name }
  const [ranksLoading, setRanksLoading] = useState(false);
  const [leaguesLoading, setLeaguesLoading] = useState(false);

  // меню действий (троеточие)
  const [menuOpenFor, setMenuOpenFor] = useState(null); // id сотрудника или null

  // состояние статуса пользователя для открытого меню + busy
  const [menuUserState, setMenuUserState] = useState(null); // { userId, isActive } | null
  const [menuBusy, setMenuBusy] = useState(false);

  // базовый origin API (отрезаем /api/vN) и резолвер относительных путей
  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) =>
    !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;

  // ------ сортировка сотрудников: кириллица → латиница; внутри — по алфавиту ------
  const startsWithCyr = (s = '') => /^[\p{Script=Cyrillic}]/u.test((s || '').trim());
  const norm = (s = '') => (s || '').trim().toLowerCase();

  const sortEmployees = (arr) => {
    const list = [...(arr || [])];
    list.sort((a, b) => {
      const an = norm(a?.name || '');
      const bn = norm(b?.name || '');
      const aC = startsWithCyr(an) ? 0 : 1;
      const bC = startsWithCyr(bn) ? 0 : 1;
      if (aC !== bC) return aC - bC;
      const cmp = an.localeCompare(bn, ['ru', 'en'], { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
    return list;
  };
  // -------------------------------------------------------------------------------

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('query', q);
      const res = await apiFetch(`${API_URL}/employees/search/?${params.toString()}`);
      const data = res.ok ? await res.json() : [];
      const arr = Array.isArray(data) ? data : [];
      setItems(sortEmployees(arr));
    } finally {
      setLoading(false);
    }
  };

  const loadRanks = async () => {
    setRanksLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/structure/ranks`);
      const data = res.ok ? await res.json() : [];
      const map = {};
      (Array.isArray(data) ? data : []).forEach((r) => {
        const id = Number(r?.id);
        const name = String(r?.name || '').trim();
        if (Number.isFinite(id) && id > 0 && name) map[id] = name;
      });
      setRanks(map);
    } finally {
      setRanksLoading(false);
    }
  };

  const loadLeagues = async () => {
    setLeaguesLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/structure/leagues`);
      const data = res.ok ? await res.json() : [];
      const map = {};
      (Array.isArray(data) ? data : []).forEach((l) => {
        const id = Number(l?.id);
        const name = String(l?.name || '').trim();
        if (Number.isFinite(id) && id > 0 && name) map[id] = name;
      });
      setLeaguesMap(map);
    } finally {
      setLeaguesLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadRanks();
    loadLeagues();
  }, []);

  // 🔎 авто-поиск с дебаунсом — 300мс
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Закрытие меню по клику вне и по Esc
  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuOpenFor) return;
      if (!e.target.closest?.(ADMIN_MENU_OUTSIDE_SELECTOR)) {
        setMenuOpenFor(null);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setMenuOpenFor(null); };

    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpenFor]);

  // Получить пользователя по employee_id через админский API
  const getUserForEmployee = async (employeeId) => {
    const trySearch = async () => {
      const res = await apiFetch(`${API_URL}/admin/users?employee_id=${employeeId}`);
      if (!res.ok) return null;
      const list = await res.json();
      const arr = Array.isArray(list) ? list : [];
      return arr.find((u) => String(u.employee_id) === String(employeeId)) || null;
    };
    const tryList = async () => {
      const res = await apiFetch(`${API_URL}/admin/users`);
      if (!res.ok) return null;
      const list = await res.json();
      const arr = Array.isArray(list) ? list : [];
      return arr.find((u) => String(u.employee_id) === String(employeeId)) || null;
    };
    return (await trySearch()) ?? (await tryList());
  };

  // Для PATCH email нужен user_id
  const getUserIdForEmployee = async (employeeId) => {
    const u = await getUserForEmployee(employeeId);
    return u?.id ?? null;
  };

  // подгружать статус пользователя, когда открываем меню для строки
  useEffect(() => {
    let aborted = false;
    (async () => {
      if (!menuOpenFor) {
        setMenuUserState(null);
        return;
      }
      try {
        const u = await getUserForEmployee(menuOpenFor);
        if (!aborted) {
          setMenuUserState(u ? { userId: u.id, isActive: !!u.is_active } : null);
        }
      } catch {
        if (!aborted) setMenuUserState(null);
      }
    })();
    return () => { aborted = true; };
  }, [menuOpenFor]);

  // блокировка / разблокировка по employee_id (через связанный user_id)
  async function blockByEmployee(employeeId, block = true) {
    const userId = await getUserIdForEmployee(employeeId);
    if (!userId) {
      alert('Для сотрудника не найдена учётная запись пользователя');
      return;
    }
    const url = `${API_URL}/admin/users/${userId}/${block ? 'block' : 'unblock'}`;
    const res = await apiFetch(url, { method: 'POST' });
    if (!res.ok) {
      alert(`Не удалось ${block ? 'заблокировать' : 'разблокировать'} пользователя`);
    } else {
      await load();
    }
  }

  const openEditEmployee = async (employee) => {
    setMenuOpenFor(null);
    const u = await getUserForEmployee(employee.id).catch(() => null);
    setEditModal({ employee, user: u || null });
  };

  // удаление сотрудника и связанного пользователя
  async function deleteEmployeeAndUser(employeeId) {
    if (!window.confirm('Удалить сотрудника и все связанные данные? Это действие необратимо.')) return;
    const res = await apiFetch(`${API_URL}/admin/users/by-employee/${employeeId}`, { method: 'DELETE' });
    if (!res.ok) {
      let t = ''; try { t = await res.text(); } catch { }
      alert(t || 'Не удалось удалить сотрудника/пользователя');
    } else {
      await load();
    }
  }

  // 🔒 ЛОК СКРОЛЛА ПРИ ОТКРЫТЫХ МОДАЛКАХ СОТРУДНИКОВ (создание/редактирование)
  useEffect(() => {
    const anyModal = !!showCreate || !!editModal;
    if (!anyModal) return;
    const y = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      const top = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, top ? -parseInt(top, 10) : 0);
    };
  }, [showCreate, editModal]);

  return (
    <div>
      <SearchInput
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Поиск сотрудника..."
        ariaLabel="Поиск сотрудника"
      />

      {loading ? (
        <p>Загрузка…</p>
      ) : (
        <ul className={s.list}>
          {items.map((it) => {
            return (
              <li
                key={it.id}
                className={`${s.row} ${s.rowInteractive}`}
                onClick={() => openEditEmployee(it)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openEditEmployee(it);
                  }
                }}
              >
                {/* Аватар слева */}
                {renderEmployeeAvatar(it, resolveImageUrl, 44)}

                {/* Основной блок просмотра */}
                <div className={s.main}>
                  <>
                    <div className={s.name}>{it.name}</div>
                    <div className={s.meta}>
                      {(() => {
                        // 1) ранг: сначала по rank_id из справочника, иначе fallback на it.rank
                        const rankId = Number(it?.rank_id);
                        const rankNameFromMap = Number.isFinite(rankId) ? ranks[rankId] : null;

                        const rankText =
                          (rankNameFromMap && String(rankNameFromMap).trim()) ||
                          (it?.rank !== null && typeof it?.rank !== 'undefined' && String(it.rank).trim() !== ''
                            ? String(it.rank).trim()
                            : 'Ранг не указан');

                        // 2) лига: только если есть league_id и мы нашли название
                        const leagueId = Number(it?.league_id);
                        const leagueName =
                          Number.isFinite(leagueId) ? (leaguesMap[leagueId] || '') : '';

                        return (
                          <>
                            <span>{rankText}</span>

                            {leagueName ? (
                              <>
                                <img src={leaguesIcon} alt="" className={s.leagueSepIcon} />
                                <span>{leagueName}</span>
                              </>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  </>
                </div>

                <div onClick={(e) => e.stopPropagation()}>
                  <AdminRowMenu
                    open={menuOpenFor === it.id}
                    onToggle={(e) => {
                      e.stopPropagation();
                      setMenuOpenFor((cur) => (cur === it.id ? null : it.id));
                    }}
                    items={[
                      {
                        key: 'edit',
                        label: 'Редактировать',
                        onClick: () => openEditEmployee(it),
                      },
                    {
                      key: 'access',
                      label: menuUserState?.isActive ? 'Закрыть доступ' : 'Открыть доступ',
                      disabled: menuBusy || (menuOpenFor === it.id && !menuUserState),
                      onClick: async () => {
                        setMenuOpenFor(null);
                        try {
                          setMenuBusy(true);
                          await blockByEmployee(it.id, !!menuUserState?.isActive);
                        } finally {
                          setMenuBusy(false);
                        }
                      },
                    },
                    {
                      key: 'delete',
                      label: 'Удалить',
                      danger: true,
                      onClick: () => {
                        setMenuOpenFor(null);
                        deleteEmployeeAndUser(it.id);
                      },
                    },
                  ]}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* NEW: полноэкранная модалка создания пользователя и сотрудника */}
      {showCreate && (
        <CreateUserEmployeeModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await load();
          }}
          ranks={ranks}
        />
      )}

      {/* полноэкранная модалка редактирования */}
      {editModal && (
        <EditUserEmployeeModal
          onClose={() => setEditModal(null)}
          onSaved={async () => {
            setEditModal(null);
            await load();
          }}
          ranks={ranks}
          employee={editModal.employee}
          user={editModal.user}
          allEmployees={items}
        />
      )}

      {/* плавающая фиолетовая кнопка (FAB) как в чатах */}
      <button
        type="button"
        className={s.fab}
        aria-label="Создать сотрудника"
        title="Создать сотрудника"
        onClick={() => setShowCreate(true)}
      >
        <span className={s.fabSvgWrap} aria-hidden="true">
          <ChatFormBubble className={s.fabBubble} />
          <div className={s.fabInner}>
            <ChatPlusIcon className={s.fabIcon} />
          </div>
        </span>
      </button>
    </div>
  );
}


/* ======================= СТРАНИЦА «ДР» ======================= */

function AdminBirthdays() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // добавили news_date в состояние редактирования
  const [edit, setEdit] = useState(null); // { item, title, body, enabled, image_path, news_date }
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [pictures, setPictures] = useState([]); // [{filename,image_path,mtime}]
  const [picMenu, setPicMenu] = useState(null); // filename | null

  // 🔎 локальный поиск с дебаунсом (по имени сотрудника)
  const [q, setQ] = useState('');
  const [filtered, setFiltered] = useState([]);

  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) =>
    !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;

  // подстановка даты публикации = (день/месяц даты рождения) + текущий год
  const deriveNewsDateFromBirthdate = (employee_birthdate) => {
    if (!employee_birthdate) return '';
    const bd = new Date(employee_birthdate); // ожидаем YYYY-MM-DD
    if (Number.isNaN(bd.getTime())) return '';
    const year = new Date().getFullYear();
    let month = bd.getMonth() + 1; // 1..12
    let day = bd.getDate();        // 1..31
    // 29 февр. → 28, если год не високосный
    const isLeap = (y) => (new Date(y, 1, 29)).getMonth() === 1;
    if (month === 2 && day === 29 && !isLeap(year)) day = 28;
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  async function load() {
    setLoading(true);
    setError('');
    try {
      const r = await apiFetch(`${API_URL}/admin/birthdays/templates`);
      const data = r.ok ? await r.json() : [];
      const list = Array.isArray(data) ? data.slice() : [];
      list.sort((a, b) => (a.days_until ?? 0) - (b.days_until ?? 0)); // ближайшие первыми
      setItems(list);
    } catch {
      setError('Не удалось загрузить шаблоны ДР');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // синхронизируем фильтрованный список при получении items
  useEffect(() => { setFiltered(items); }, [items]);

  // дебаунс-фильтрация по имени
  useEffect(() => {
    const t = setTimeout(() => {
      const val = (q || '').trim().toLowerCase();
      if (!val) { setFiltered(items); return; }
      setFiltered(items.filter(x =>
        (x.employee_fullname || '').toLowerCase().includes(val)
      ));
    }, 300);
    return () => clearTimeout(t);
  }, [q, items]);

  // 🔒 ЛОК СКРОЛЛА ПРИ ОТКРЫТОЙ МОДАЛКЕ РЕДАКТИРОВАНИЯ ДР
  useEffect(() => {
    if (!edit) return;
    const y = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      const top = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, top ? -parseInt(top, 10) : 0);
    };
  }, [edit]);

  const openEdit = (card) => {
    setEdit({
      item: card,
      title: card.title || '',
      body: card.body || '',
      enabled: card.enabled !== false,
      image_path: card.image_path || null,
      // авто: если news_date нет — берём ДР сотрудника + текущий год
      news_date: card.news_date || deriveNewsDateFromBirthdate(card.employee_birthdate),
    });
  };

  const saveEdit = async () => {
    if (!edit?.item) return;
    const id = edit.item.employee_id;
    try {
      const payload = {
        title: edit.title,
        body: edit.body,
        enabled: !!edit.enabled,
        image_path: edit.image_path || null,
        ...(edit.news_date ? { news_date: edit.news_date } : {}), // отправляем только если заполнено
      };

      const r = await apiFetch(`${API_URL}/admin/birthdays/templates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      setEdit(null);
      load();
    } catch {
      alert('Не удалось сохранить карточку');
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const r = await apiFetch(`${API_URL}/admin/birthdays/upload`, {
      method: 'POST',
      body: fd,
    });
    if (r.ok) {
      const json = await r.json();
      setEdit((prev) => ({ ...prev, image_path: json.image_path || null }));
    } else {
      alert('Не удалось загрузить изображение');
    }
  };
  const loadPictures = async () => {
    const r = await apiFetch(`${API_URL}/admin/birthdays/pictures`);
    if (!r.ok) { setPictures([]); return; }
    const json = await r.json();
    setPictures(Array.isArray(json?.items) ? json.items : []);
  };

  const openGallery = async () => {
    await loadPictures();
    setPicMenu(null);
    setGalleryOpen(true);
  };

  const choosePicture = (image_path) => {
    setEdit((prev) => prev ? ({ ...prev, image_path }) : prev);
    setGalleryOpen(false);
  };

  const deletePicture = async (filename) => {
    if (!window.confirm('Удалить картинку?')) return;
    const r = await apiFetch(`${API_URL}/admin/birthdays/pictures/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    });
    if (!r.ok) {
      alert('Не удалось удалить картинку');
      return;
    }
    // если удалили текущую выбранную — сбросим
    setEdit((prev) => {
      if (!prev?.image_path) return prev;
      const base = '/uploads/birthday_pictures/';
      if (prev.image_path === `${base}${filename}`) return { ...prev, image_path: null };
      return prev;
    });
    await loadPictures();
    setPicMenu(null);
  };

  const cut = (s, n = 50) => {
    const t = String(s || '').replace(/\s+/g, ' ').trim();
    return t.length > n ? t.slice(0, n) + '…' : t;
  };

  const renderList = (list) => (
    <ul className={ns.list}>
      {list.map((x) => {
        const img = resolveImageUrl(x.image_path);
        const dateStr = x.employee_birthdate
          ? new Date(x.employee_birthdate).toLocaleDateString()
          : '';
        return (
          <li
            key={x.employee_id}
            className={ns.row}
            onClick={() => openEdit(x)}
            role="button"
            title={x.employee_fullname}
          >
            <div className={ns.thumbOuter}>
              <div className={ns.thumbClip}>
                {img ? (
                  <img src={img} alt="" className={ns.thumb} loading="lazy" />
                ) : (
                  <div className={ns.thumbPlaceholder} />
                )}
              </div>
            </div>

            <div className={ns.content}>
              {/* Верхняя строка: имя слева (крупно) и дата справа */}
              <div className={ns.meta}>
                <span className={ns.title} style={{ margin: 0, lineHeight: '18px' }}>
                  {x.employee_fullname}
                </span>
                <time className={ns.date}>{dateStr}</time>
              </div>

              {/* Превью: body, обрезанный до 50 символов */}
              {x.body && (
                <div style={{ color: '#555', fontSize: 12, lineHeight: 1.3, marginTop: 2 }}>
                  {cut(x.body, 50)}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={s.section}>
      <SearchInput
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Поиск по фио сотрудника..."
        ariaLabel="Поиск по ФИО сотрудника"
      />

      {loading && <p>Загрузка…</p>}
      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
      {!loading && renderList(filtered)}

      {/* Модалка редактирования карточки ДР */}
      {edit && (
        <div
          className={s.modalBackdrop}
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEdit(null);
          }}
        >
          <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <strong>Редактировать</strong>
              <button
                className={s.modalClose}
                onClick={() => setEdit(null)}
                aria-label="Закрыть"
              >
                <CloseIcon className={s.closeIcon} />
              </button>
            </div>

            <div className={s.createForm}>
              <label className={s.label}>Заголовок</label>
              <Input
                value={edit.title}
                onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                ariaLabel="Заголовок"
              />

              <label className={s.label}>Дата выхода новости</label>
              <DateInput
                value={edit.news_date || ''}
                onChange={(e) => setEdit({ ...edit, news_date: e.target.value })}
                ariaLabel="Дата выхода новости"
              />

              <label className={s.label}>Текст поздравления</label>
              <Textarea
                className={s.birthdayText}
                value={edit.body}
                onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                rows={5}
                ariaLabel="Текст поздравления"
              />

              <label className={s.label}>Картинка</label>
              {/* НОВОЕ: блок для загрузки и полноширинного превью */}
              <div className={s.imageBlock}>

                {edit.image_path && (
                  <img
                    src={resolveImageUrl(edit.image_path)}
                    alt=""
                    className={s.imageWide}
                  />
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <label className={s.uploadBtn} title="Загрузить картинку" aria-label="Загрузить картинку">
                    <input
                      type="file"
                      accept="image/*"
                      className={s.hiddenInput}
                      onChange={(e) => uploadImage(e.target.files?.[0] || null)}
                    />
                    <UploadChatPictureIcon className={s.uploadIcon} />
                  </label>

                  <button
                    type="button"
                    className={s.uploadBtn}
                    title="Открыть папку birthday_pictures"
                    aria-label="Открыть папку birthday_pictures"
                    onClick={openGallery}
                  >
                    <FolderIcon className={s.uploadIcon} />
                  </button>
                </div>
              </div>
            </div>
            {galleryOpen && (
              <div
                className={s.modalBackdrop}
                role="dialog"
                aria-modal="true"
                onClick={(e) => { if (e.target === e.currentTarget) setGalleryOpen(false); }}
                style={{ zIndex: 2000 }}
              >
                <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
                  <div className={s.modalHeader}>
                    <strong>Картинки ДР</strong>
                    <button className={s.modalClose} onClick={() => setGalleryOpen(false)} aria-label="Закрыть">
                      <CloseIcon className={s.closeIcon} />
                    </button>
                  </div>

                  <div className={s.modalBody}>
                    {pictures.length === 0 ? (
                      <p className={s.modalEmpty}>Папка пустая</p>
                    ) : (
                      <div className={s.picturesGrid}>
                        {pictures.map((p) => {
                          const url = resolveImageUrl(p.image_path);
                          const open = picMenu === p.filename;
                          return (
                            <div key={p.filename} style={{ position: 'relative' }}>
                              <button
                                type="button"
                                onClick={() => setPicMenu(open ? null : p.filename)}
                                style={{
                                  border: 'none',
                                  padding: 0,
                                  background: 'transparent',
                                  width: '100%',
                                  cursor: 'pointer',
                                }}
                                title={p.filename}
                              >
                                <img
                                  src={url}
                                  alt=""
                                  loading="lazy"
                                  style={{
                                    width: '100%',
                                    aspectRatio: '16 / 9',
                                    objectFit: 'cover',
                                    borderRadius: 12,
                                    display: 'block',
                                  }}
                                />
                              </button>

                              {open && (
                                <div className={s.picActions}>
                                  <button
                                    type="button"
                                    className={s.picActionBtn}
                                    title="Выбрать"
                                    aria-label="Выбрать"
                                    onClick={() => choosePicture(p.image_path)}
                                  >
                                    <CheckIcon className={s.picActionIcon} />
                                  </button>

                                  <button
                                    type="button"
                                    className={s.picActionBtn}
                                    title="Удалить"
                                    aria-label="Удалить"
                                    onClick={() => deletePicture(p.filename)} // внутри deletePicture оставь confirm
                                  >
                                    <TrashIcon className={s.picActionIcon} />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className={s.modalFooter}>
              <button className={s.btnCancel} onClick={() => setEdit(null)}>
                Отмена
              </button>
              <button className={s.btnPrimary} onClick={saveEdit}>
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================= СТРАНИЦА «Ачивки» ======================= */

function AdminAchievements() {
  const subTabs = [
    { key: 'medal', label: 'Медали' },
    { key: 'label', label: 'Метки' },
    { key: 'award', label: 'Достижения' },
  ];
  const [type, setType] = useState('medal');            // medal|label|award
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list'); // list | table

  // локальный резолвер ссылок, как в разделах Структуры/Команд
  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) =>
    !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;

  const [menuOpen, setMenuOpen] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);   // AchievementOut или null
  const [assignOpen, setAssignOpen] = useState(null);   // {id} — выдача сотрудникам

  // --- DnD для порядка ачивок ---
  const [drag, setDrag] = useState({
    active: false,
    fromIndex: 0,
    overIndex: 0,
    startY: 0,
    lastY: 0,
    itemHeight: 0,
    pointerId: null,
  });
  const [savingOrder, setSavingOrder] = useState(false);

  const moveItem = (arr, from, to) => {
    const list = arr.slice();
    const [x] = list.splice(from, 1);
    list.splice(to, 0, x);
    return list;
  };

  const startDragFromIcon = (e, index) => {
    const pointerId = e.pointerId ?? 'mouse';
    e.currentTarget.setPointerCapture?.(pointerId);
    const row = e.currentTarget.closest('li');
    const h = (row && row.offsetHeight) || 60;

    setDrag({
      active: true,
      fromIndex: index,
      overIndex: index,
      startY: e.clientY,
      lastY: e.clientY,
      itemHeight: h,
      pointerId,
    });
    // чтобы на тач-экранах не было скролла
    document.body.style.touchAction = 'none';
  };

  const onDragMove = (e) => {
    if (!drag.active) return;
    const dy = e.clientY - drag.startY;
    const steps = Math.floor(Math.abs(dy) / Math.max(1, drag.itemHeight));
    let target = drag.fromIndex + (dy > 0 ? steps : -steps);
    target = Math.max(0, Math.min(items.length - 1, target));
    if (target !== drag.overIndex) {
      setItems(prev => moveItem(prev, drag.overIndex, target));
      setDrag(d => ({ ...d, overIndex: target, lastY: e.clientY }));
    }
  };

  const endDrag = async () => {
    if (!drag.active) return;
    setDrag(d => ({ ...d, active: false }));
    document.body.style.touchAction = '';

    // сохраняем порядок на бэкенде
    setSavingOrder(true);
    try {
      await apiFetch(`${API_URL}/achievements/reorder?type=${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: items.map(x => x.id) }),
      });
      // локально проставим position «как есть»
      setItems(prev => prev.map((it, i) => ({ ...it, position: i + 1 })));
    } catch (e) {
      // если что-то пошло не так — перечитаем список
      try {
        const res = await apiFetch(`${API_URL}/achievements?type=${type}`);
        const data = res.ok ? await res.json() : [];
        setItems(Array.isArray(data) ? data : []);
      } catch { }
    } finally {
      setSavingOrder(false);
    }
  };



  // 🔒 ЛОК СКРОЛЛА ПРИ ОТКРЫТЫХ МОДАЛКАХ АЧИВОК (создание / редактирование / выдача)
  useEffect(() => {
    const anyModal = !!createOpen || !!editItem || !!assignOpen;
    if (!anyModal) return;

    const y = window.scrollY;
    const body = document.body;

    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      const top = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, top ? -parseInt(top, 10) : 0);
    };
  }, [createOpen, editItem, assignOpen]);

  useEffect(() => {
    if (!drag.active) return;
    const move = (ev) => onDragMove(ev);
    const up = () => endDrag();

    document.addEventListener('pointermove', move, { passive: false });
    document.addEventListener('pointerup', up, { once: true });
    document.addEventListener('pointercancel', up, { once: true });

    return () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);


  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuOpen) return;
      if (!e.target.closest?.(ADMIN_MENU_OUTSIDE_SELECTOR)) {
        setMenuOpen(null);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setMenuOpen(null); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/achievements?type=${type}`);
      const list = res.ok ? await res.json() : [];
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      // сетевой сбой / CORS / падение бэка — не роняем весь раздел
      // а просто показываем пустой список и логируем в консоль
      // (в деве будет видна ошибка в console.error)
      // eslint-disable-next-line no-console
      console.error('Не удалось загрузить список ачивок', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  async function remove(id) {
    if (!window.confirm('Удалить ачивку и все выдачи?')) return;
    const r = await apiFetch(`${API_URL}/achievements/${id}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось удалить'); return; }
    await load();
  }

  const filteredItems = items.filter((a) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const shortName = String(a.short_name || '').toLowerCase();
    const fullName = String(a.full_name || '').toLowerCase();
    const desc = String(a.description || '').toLowerCase();
    return shortName.includes(q) || fullName.includes(q) || desc.includes(q);
  });
  const canReorder = viewMode === 'list' && !search.trim();

  return (
    <div className={s.section}>
      <ScrollTabs items={subTabs} activeKey={type} onChange={setType} />

      <div className={s.achTopBar}>
        <div className={s.achSearchWrap}>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по названию или описанию"
            ariaLabel="Поиск ачивок"
          />
        </div>
        <div className={s.achViewSwitch} role="group" aria-label="Режим отображения">
          <button
            type="button"
            className={`${s.achViewBtn} ${viewMode === 'list' ? s.achViewBtnActive : ''}`}
            onClick={() => setViewMode('list')}
            aria-label="Список"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M21 12L9 12M21 6L9 6M21 18L9 18M5 12C5 12.5523 4.55228 13 4 13C3.44772 13 3 12.5523 3 12C3 11.4477 3.44772 11 4 11C4.55228 11 5 11.4477 5 12ZM5 6C5 6.55228 4.55228 7 4 7C3.44772 7 3 6.55228 3 6C3 5.44772 3.44772 5 4 5C4.55228 5 5 5.44772 5 6ZM5 18C5 18.5523 4.55228 19 4 19C3.44772 19 3 18.5523 3 18C3 17.4477 3.44772 17 4 17C4.55228 17 5 17.4477 5 18Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span className={s.achViewBtnText}>Список</span>
          </button>
          <button
            type="button"
            className={`${s.achViewBtn} ${viewMode === 'table' ? s.achViewBtnActive : ''}`}
            onClick={() => setViewMode('table')}
            aria-label="Таблица"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3 12H21M12 3V21M7.8 3H16.2C17.8802 3 18.7202 3 19.362 3.32698C19.9265 3.6146 20.3854 4.07354 20.673 4.63803C21 5.27976 21 6.11984 21 7.8V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V7.8C3 6.11984 3 5.27976 3.32698 4.63803C3.6146 4.07354 4.07354 3.6146 4.63803 3.32698C5.27976 3 6.11984 3 7.8 3Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span className={s.achViewBtnText}>Таблица</span>
          </button>
        </div>
      </div>

      {loading && <p>Загрузка…</p>}
      {viewMode === 'list' ? (
        <ul className={s.list}>
          {filteredItems.map((a) => (
            <li
              key={a.id}
              className={`${s.row} ${s.rowInteractive} ${drag.active && items.findIndex(x => x.id === a.id) === drag.overIndex ? s.rowDragging : ''}`}
              onClick={() => setEditItem(a)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setEditItem(a);
                }
              }}
            >
              {/* слева — иконка/картинка (ручка для перетаскивания) */}
              <div
                className={s.achDragHandle}
                style={{
                  width: 52, minWidth: 52, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  cursor: canReorder ? (drag.active ? 'grabbing' : 'grab') : 'default'
                }}
                onClick={(ev) => ev.stopPropagation()}
                onPointerDown={(ev) => {
                  if (!canReorder) return;
                  startDragFromIcon(ev, items.findIndex(x => x.id === a.id));
                }}
                title={canReorder ? 'Перетащите, чтобы изменить порядок' : 'Сбросьте поиск для изменения порядка'}
                aria-label="Изменить порядок"
              >
                {a.image ? (
                  <img
                    src={resolveImageUrl(a.image)}
                    alt=""
                    style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 999, pointerEvents: 'none' }}
                    draggable={false}
                  />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: 16, background: '#EEE' }} />
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={s.name}>{a.short_name || 'Без названия'}</div>
                <div className={s.meta}>
                  {a.full_name ? a.full_name : (type === 'medal' ? 'Медаль' : type === 'label' ? 'Метка' : 'Достижение')}
                </div>
              </div>

              <AdminRowMenu
                open={menuOpen === a.id}
                onToggle={(e) => {
                  e.stopPropagation();
                  setMenuOpen(menuOpen === a.id ? null : a.id);
                }}
                items={[
                  { key: 'edit', label: 'Редактировать', onClick: () => { setMenuOpen(null); setEditItem(a); } },
                  { key: 'assign', label: 'Выдать', onClick: () => { setMenuOpen(null); setAssignOpen({ id: a.id }); } },
                  { key: 'delete', label: 'Удалить', danger: true, onClick: () => { setMenuOpen(null); remove(a.id); } },
                ]}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className={s.achCardsGrid}>
          {filteredItems.map((a) => (
            <article
              key={a.id}
              className={s.achCard}
              onClick={() => setEditItem(a)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setEditItem(a);
                }
              }}
            >
              <AdminRowMenu
                open={menuOpen === a.id}
                onToggle={(e) => {
                  e.stopPropagation();
                  setMenuOpen(menuOpen === a.id ? null : a.id);
                }}
                wrapClassName={s.achCardMenu}
                items={[
                  { key: 'edit', label: 'Редактировать', onClick: () => { setMenuOpen(null); setEditItem(a); } },
                  { key: 'assign', label: 'Выдать', onClick: () => { setMenuOpen(null); setAssignOpen({ id: a.id }); } },
                  { key: 'delete', label: 'Удалить', danger: true, onClick: () => { setMenuOpen(null); remove(a.id); } },
                ]}
              />

              <div className={s.achCardAvatarWrap}>
                {a.image ? (
                  <img
                    src={resolveImageUrl(a.image)}
                    alt=""
                    className={s.achCardAvatar}
                    draggable={false}
                  />
                ) : (
                  <div className={s.achCardAvatarPlaceholder} />
                )}
              </div>

              <div className={s.achCardTitle}>{a.short_name || 'Без названия'}</div>
              <div className={s.achCardSubtitle}>{a.full_name || '—'}</div>
            </article>
          ))}
        </div>
      )}

      {!loading && filteredItems.length === 0 && (
        <div className={s.meta} style={{ marginTop: 10 }}>Ничего не найдено</div>
      )}

      {/* FAB — создать */}
      <button
        type="button"
        className={s.fab}
        aria-label="Создать ачивку"
        title="Создать ачивку"
        onClick={() => setCreateOpen(true)}
      >
        <span className={s.fabSvgWrap} aria-hidden="true">
          <FabBubble className={s.fabBubble} />
          <div className={s.fabInner}><AddIconPlus className={s.fabIcon} /></div>
        </span>
      </button>

      {createOpen && (
        <AchievementModal
          title="Новая ачивка"
          initial={{
            type,
            short_name: '',
            full_name: '',
            description: '',
            image: null,
            reset_day: ''
          }}
          onCancel={() => setCreateOpen(false)}
          onSubmit={async (payload) => {
            const fd = new FormData();
            fd.append('type', payload.type);
            fd.append('short_name', payload.short_name);
            if (payload.full_name) fd.append('full_name', payload.full_name);
            if (payload.description) fd.append('description', payload.description);
            if (payload.reset_day?.toString().trim()) fd.append('reset_day', String(payload.reset_day));
            if (payload.image) fd.append('image', payload.image);
            // первичная выдача (необязательно)
            (payload.assignees || []).forEach(a => {
              const id = typeof a === 'object' ? a.id : a;
              fd.append('assignee_ids', String(id));
            });
            if (payload.awarded_at) fd.append('awarded_at', payload.awarded_at);

            const r = await apiFetch(`${API_URL}/achievements`, { method: 'POST', body: fd });
            if (!r.ok) { alert('Не удалось создать'); return; }
            setCreateOpen(false);
            await load();
          }}
        />
      )}

      {editItem && (
        <AchievementModal
          title="Редактировать ачивку"
          achievementId={editItem.id}
          initial={{
            type: editItem.type,
            short_name: editItem.short_name || '',
            full_name: editItem.full_name || '',
            description: editItem.description || '',
            reset_day: (editItem.reset_day ?? ''),
            image: null,
            image_url: editItem.image || null,   // превью текущей картинки
            hideAssignees: false,                // в редактировании управляем выдачами
          }}
          onCancel={() => setEditItem(null)}
          onSubmit={async (payload) => {
            const fd = new FormData();
            if (payload.type) fd.append('type', payload.type);
            if (payload.short_name) fd.append('short_name', payload.short_name);
            fd.append('full_name', payload.full_name || '');
            fd.append('description', payload.description || '');
            fd.append('reset_day', String(payload.reset_day ?? ''));
            if (payload.image) fd.append('image', payload.image);

            const r = await apiFetch(`${API_URL}/achievements/${editItem.id}`, { method: 'PATCH', body: fd });
            if (!r.ok) { alert('Не удалось сохранить'); return; }
            setEditItem(null);
            await load();
          }}
        />
      )}

      {assignOpen && (
        <AssignModal
          title="Выдать ачивку"
          onCancel={() => setAssignOpen(null)}
          onSubmit={async ({ employee_ids, awarded_at }) => {
            if (!employee_ids?.length) { setAssignOpen(null); return; }
            const fd = new FormData();
            employee_ids.forEach(id => fd.append('employee_ids', String(id)));
            if (awarded_at) fd.append('awarded_at', awarded_at);
            const r = await apiFetch(`${API_URL}/achievements/${assignOpen.id}/awards`, { method: 'POST', body: fd });
            if (!r.ok) { alert('Не удалось выдать'); return; }
            setAssignOpen(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/* ======================= МОДАЛКА Ачивки (create/edit) ======================= */
function AchievementModal({ title, initial, onCancel, onSubmit, achievementId }) {
  const [type, setType] = useState(initial?.type || 'medal');
  const [shortName, setShortName] = useState(initial?.short_name || '');
  const [fullName, setFullName] = useState(initial?.full_name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [resetDay, setResetDay] = useState(initial?.reset_day ?? '');

  // файл и превью справа
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);                // для нового файла
  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) =>
    !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;
  const initialPreview = initial?.image_url ? resolveImageUrl(initial.image_url) : null;

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  // holders (кому выдано)
  const [holders, setHolders] = useState([]);       // [{ id(award_id), employee:{id,name,avatar} }]
  const [holdersLoading, setHoldersLoading] = useState(false);

  // выбор новых получателей
  const [assignees, setAssignees] = useState([]);   // [{id,name,avatar}]
  const [awardedAt, setAwardedAt] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const searchInputRef = useRef(null);
  const hideAssignees = !!initial?.hideAssignees;

  // загрузить текущие выдачи в режиме редактирования (НОВЫЙ путь)
  useEffect(() => {
    if (!achievementId) return;
    let abort = false;
    (async () => {
      setHoldersLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/achievements/${achievementId}/awards`);
        const js = r.ok ? await r.json() : [];
        if (!abort) setHolders(Array.isArray(js) ? js : []);
      } catch (e) {
        if (!abort) {
          // eslint-disable-next-line no-console
          console.error('Не удалось загрузить выдачи ачивки', e);
          setHolders([]);
        }
      } finally {
        if (!abort) setHoldersLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [achievementId]);

  // поиск сотрудников (дебаунс)
  useEffect(() => {
    const t = setTimeout(async () => {
      const q = query.trim();
      if (!q) { setResults([]); return; }
      try {
        const r = await apiFetch(`${API_URL}/employees/search/?query=${encodeURIComponent(q)}`);
        const data = r.ok ? await r.json() : [];
        const arr = Array.isArray(data) ? data : [];
        const filtered = arr.filter(e => !assignees.some(a => String(a.id) === String(e.id)));
        setResults(filtered);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, assignees]);

  const addAssignee = (emp) => {
    setAssignees(prev => [...prev, { id: emp.id, name: emp.name, avatar: emp.avatar || null }]);
    setQuery('');
    setResults([]);
    searchInputRef.current?.focus();
  };

  const removeAssignee = (id) => {
    setAssignees(prev => prev.filter(a => a.id !== id));
  };

  // выдать выбранным (НОВЫЙ путь)
  const assignSelected = async () => {
    if (!achievementId || assignees.length === 0) return;
    const fd = new FormData();
    assignees.forEach(a => fd.append('employee_ids', String(a.id)));
    if (awardedAt) fd.append('awarded_at', awardedAt);
    const r = await apiFetch(`${API_URL}/achievements/${achievementId}/awards`, {
      method: 'POST',
      body: fd,
    });

    if (!r.ok) { alert('Не удалось выдать'); return; }
    // обновим список holders
    const ref = await apiFetch(`${API_URL}/achievements/${achievementId}/awards`);
    const js = ref.ok ? await ref.json() : [];
    setHolders(Array.isArray(js) ? js : []);
    // очистим выбор
    setAssignees([]);
    setQuery('');
    setResults([]);
    setAwardedAt('');
  };

  // снять ачивку (удалить выдачу)
  const revoke = async (awardId) => {
    if (!achievementId || !awardId) return;
    const r = await apiFetch(`${API_URL}/achievements/${achievementId}/awards/${awardId}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось снять ачивку'); return; }
    setHolders(holders.filter(h => h.id !== awardId));
  };

  return (
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <div className={s.createForm}>
          <div className={s.pillRow} style={{ marginBottom: 8 }}>
            {['medal', 'label', 'award'].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`${s.pill} ${type === t ? s.pillActive : ''}`}
              >
                {t === 'medal' ? 'Медаль' : t === 'label' ? 'Метка' : 'Достижение'}
              </button>
            ))}
          </div>

          <div className={s.createGridRowImage}>
            <div className={s.imageRow}>
              <label
                title={preview || initialPreview ? 'Сменить изображение' : 'Загрузить изображение'}
                className={s.imagePicker}
              >
                <input
                  type="file"
                  accept="image/*"
                  className={s.hiddenInput}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setImage(f);
                    if (preview) URL.revokeObjectURL(preview);
                    setPreview(f ? URL.createObjectURL(f) : null);
                  }}
                />
                {(preview || initialPreview) ? (
                  <img src={preview || initialPreview} alt="" className={s.imageThumb} />
                ) : (
                  <div className={s.imageEmptyState}>
                    <UploadChatPictureIcon className={s.uploadIcon} />
                    <span className={s.imageEmptyText}>Нажмите, чтобы загрузить</span>
                  </div>
                )}
              </label>
            </div>

            <div className={s.createGridRow}>
              <div>
                <label className={s.label}>Краткое название</label>
                <Input value={shortName} onChange={(e) => setShortName(e.target.value)} ariaLabel="Краткое название" />
              </div>
              <div>
                <label className={s.label}>Полное название</label>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} ariaLabel="Полное название" />
              </div>
            </div>
          </div>

          <div>
            <label className={s.label}>Описание</label>
            <Textarea
              className={s.fieldInput}
              rows={4}
              value={description}
              onChange={e => setDescription(e.target.value)}
              ariaLabel="Описание"
            />
          </div>
          <div>
            <div className={s.resetDayLabelRow}>
              <label className={s.label}>Обнуление (день месяца)</label>
              <span className={s.helpTooltipWrap}>
                <button
                  type="button"
                  className={s.helpTooltipBtn}
                  aria-label="Что значит обнуление"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path
                      d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13M12 17H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <span className={s.helpTooltipBubble} role="tooltip">
                  Ачивка снимается в этот день следующего месяца после выдачи.
                </span>
              </span>
            </div>
            <Input
              type="number"
              min={1}
              max={31}
              value={resetDay}
              onChange={(e) => setResetDay(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="например 5"
              ariaLabel="Обнуление (день месяца)"
            />
          </div>
          {!hideAssignees && (
            <>
              {/* У кого есть */}
              <label className={s.label}>У кого есть</label>
              {holdersLoading ? (
                <div className={s.meta}>Загрузка…</div>
              ) : (
                <div className={s.selectedTags} style={{ marginTop: 6 }}>
                  {holders.length === 0 && <div className={s.meta}>Пока ни у кого</div>}
                  {holders.map(h => (
                    <span key={h.id} className={s.tag}>
                      {renderEmployeeAvatar(h.employee, resolveImageUrl)}
                      {h.employee?.name || `#${h.employee_id}`}
                      <button
                        type="button"
                        className={s.tagRemove}
                        title="Снять ачивку"
                        onClick={() => revoke(h.id)}
                      >&times;</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Выдать ещё */}
              <label className={s.label} style={{ marginTop: 8 }}>Выдать сотруднику</label>

              {assignees.length > 0 && <div className={s.selectedTags}>
                {assignees.map(a => (
                  <span key={a.id} className={s.tag}>
                    {renderEmployeeAvatar(a, resolveImageUrl)}
                    {a.name}
                    <button
                      type="button"
                      className={s.tagRemove}
                      onClick={() => removeAssignee(a.id)}
                      aria-label="Убрать исполнителя"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>}

              <div className={s.searchContainer}>
                <SearchInput
                  inputRef={searchInputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Поиск сотрудника по ФИО"
                  ariaLabel="Поиск сотрудника по ФИО"
                />
                {results.length > 0 && query.trim().length > 0 && (
                  <ul className={s.searchDropdown}>
                    {results.map((emp) => (
                      <li
                        key={emp.id}
                        className={s.searchItem}
                        onClick={() => addAssignee(emp)}
                      >
                        {renderEmployeeAvatar(emp, resolveImageUrl)}
                        {emp.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" className={s.btnPrimary} onClick={assignSelected} disabled={assignees.length === 0}>
                  Выдать
                </button>
              </div>
            </>
          )}
        </div>

        <div className={s.modalFooter}>
          <button className={s.btnCancel} onClick={onCancel}>Отменить</button>
          <button
            className={s.btnPrimary}
            onClick={() => onSubmit({
              type, short_name: shortName.trim(), full_name: fullName.trim(),
              description: description.trim(), image,
              assignees,
              reset_day: resetDay,
              awarded_at: awardedAt || null
            })}
            disabled={!shortName.trim()}
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}

/* ======================= МОДАЛКА Выдачи ======================= */
function AssignModal({ title, onCancel, onSubmit }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]); // [{id,name,avatar}]
  const [awardedAt, setAwardedAt] = useState('');
  const inputRef = useRef(null);

  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (p) =>
    !p ? null : /^https?:\/\//i.test(p) ? p : `${API_BASE}${p}`;

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = query.trim();
      if (!q) { setResults([]); return; }
      try {
        const r = await apiFetch(`${API_URL}/employees/search/?query=${encodeURIComponent(q)}`);
        const data = r.ok ? await r.json() : [];
        const arr = Array.isArray(data) ? data : [];
        const filtered = arr.filter(e => !selected.some(s => String(s.id) === String(e.id)));
        setResults(filtered);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, selected]);

  const add = (emp) => {
    setSelected(prev => [...prev, { id: emp.id, name: emp.name, avatar: emp.avatar || null }]);
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };
  const remove = (id) => setSelected(prev => prev.filter(s => s.id !== id));

  return (
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <div className={s.createForm}>
          <label className={s.label}>Исполнители</label>

          {selected.length > 0 && <div className={s.selectedTags} style={{ marginTop: 6 }}>
            {selected.map(a => (
              <span key={a.id} className={s.tag}>
                {renderEmployeeAvatar(a, resolveImageUrl)}
                {a.name}
                <button type="button" className={s.tagRemove} onClick={() => remove(a.id)}>&times;</button>
              </span>
            ))}
          </div>}

          <div className={s.searchContainer}>
            <SearchInput
              inputRef={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск сотрудника"
              ariaLabel="Поиск сотрудника"
            />
            {results.length > 0 && query.trim() && (
              <ul className={s.searchDropdown}>
                {results.map(emp => (
                  <li key={emp.id} className={s.searchItem} onClick={() => add(emp)}>
                    {renderEmployeeAvatar(emp, resolveImageUrl)}
                    {emp.name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className={s.label}>Дата вручения (опц.)</label>
          <Input
            className={s.fieldInput}
            type="datetime-local"
            value={awardedAt}
            onChange={(e) => setAwardedAt(e.target.value)}
            ariaLabel="Дата вручения"
          />
        </div>

        <div className={s.modalFooter}>
          <button className={s.btnCancel} onClick={onCancel}>Отменить</button>
          <button
            className={s.btnPrimary}
            onClick={() => onSubmit({ employee_ids: selected.map(s => s.id), awarded_at: awardedAt || null })}
            disabled={selected.length === 0}
          >
            Выдать
          </button>
        </div>
      </div>
    </div>
  );
}

/* ======================= СТРАНИЦА «Структура» с вложенным таббаром ======================= */

function AdminStructure() {
  const { hasPermission } = useRoleActions();
  const canManageStructure = hasPermission('structure.manage');

  const [sub, setSub] = useState('departments'); // 'departments' | 'ranks' | 'teams' | 'leagues' | 'temporaryTeams'
  const subTabs = [
    { key: 'departments', label: 'Отделы' },
    { key: 'ranks', label: 'Ранги' },
    { key: 'teams', label: 'Команды' },
    { key: 'temporaryTeams', label: 'Временные команды' },
    { key: 'leagues', label: 'Лиги' },
  ];

  // общее: поиск сотрудников для поля «руководитель»
  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) => !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;


  /* ---------- ОТДЕЛЫ ---------- */
  const [deps, setDeps] = useState([]);
  const depsRef = useRef([]);
  useEffect(() => { depsRef.current = deps; }, [deps]);

  const [depsLoading, setDepsLoading] = useState(false);

  const [depDrag, setDepDrag] = useState({
    active: false,
    fromIndex: 0,
    overIndex: 0,
    startY: 0,
    itemHeight: 0,
    pointerId: null,
  });
  const depDragRef = useRef(depDrag);
  useEffect(() => { depDragRef.current = depDrag; }, [depDrag]);

  const [depSavingOrder, setDepSavingOrder] = useState(false);

  const moveItem = (arr, from, to) => {
    const list = arr.slice();
    const [x] = list.splice(from, 1);
    list.splice(to, 0, x);
    return list;
  };

  const startDepDragFromCode = (e, index) => {
    if (!canManageStructure) return;
    if (index < 0) return;
    e.preventDefault();
    e.stopPropagation();
    setDepMenuOpen(null);
    const pointerId = e.pointerId ?? 'mouse';
    e.currentTarget.setPointerCapture?.(pointerId);
    const row = e.currentTarget.closest('li');
    const h = (row && row.offsetHeight) || 60;

    setDepDrag({
      active: true,
      fromIndex: index,
      overIndex: index,
      startY: e.clientY,
      itemHeight: h,
      pointerId,
    });
    document.body.style.touchAction = 'none';
  };

  const onDepDragMove = (e) => {
    const d = depDragRef.current;
    if (!d.active) return;

    const dy = e.clientY - d.startY;
    const h = Math.max(1, d.itemHeight);
    const steps = Math.floor((Math.abs(dy) + h / 2) / h); // порог ~ половина строки
    let target = d.fromIndex + (dy > 0 ? steps : -steps);

    const len = depsRef.current.length;
    target = Math.max(0, Math.min(len - 1, target));

    if (target !== d.overIndex) {
      setDeps(prev => moveItem(prev, d.overIndex, target));
      setDepDrag(prev => ({ ...prev, overIndex: target }));
    }
  };

  const endDepDrag = async () => {
    if (!canManageStructure) return;
    if (!depDrag.active) return;
    setDepDrag(d => ({ ...d, active: false }));
    document.body.style.touchAction = '';

    setDepSavingOrder(true);
    const ids = depsRef.current.map(x => x.id).filter(id => id > 0);
    try {
      const r = await apiFetch(`${API_URL}/structure/departments/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!r.ok) throw new Error(`reorder failed: ${r.status}`);
    } catch (e) {
      try { await loadDeps(); } catch { }
    } finally {
      setDepSavingOrder(false);
    }
  };


  const [empNameCache, setEmpNameCache] = useState({}); // { [id]: name }
  const [depMenuOpen, setDepMenuOpen] = useState(null);

  const [depCreateOpen, setDepCreateOpen] = useState(false);
  const [depEdit, setDepEdit] = useState(null); // {id, code, name, manager_id, description}

  const loadDeps = useCallback(async () => {
    setDepsLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/structure/departments`);
      const list = r.ok ? await r.json() : [];
      const arr = Array.isArray(list) ? list : [];

      setDeps(arr); // порядок сервера

      // обогащаем именами руководителей
      const ids = [...new Set(arr.map(d => d.manager_id).filter(id => typeof id === 'number'))];
      const missing = ids.filter(id => !empNameCache[id]);

      if (missing.length) {
        const pairs = await Promise.all(missing.map(async (id) => {
          const rr = await apiFetch(`${API_URL}/employees/${id}`);
          const emp = rr.ok ? await rr.json() : null;
          return [id, emp?.name || `#${id}`];
        }));
        setEmpNameCache(prev => {
          const next = { ...prev };
          for (const [id, name] of pairs) next[id] = name;
          return next;
        });
      }
    } finally {
      setDepsLoading(false);
    }
  }, [empNameCache]);
  useEffect(() => { if (sub === 'departments') loadDeps(); }, [sub, loadDeps]);

  async function createDep(payload) {
    const r = await apiFetch(`${API_URL}/structure/departments`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    if (r.status === 409) { alert('Отдел с таким code уже существует'); return; }
    if (!r.ok) { alert('Не удалось создать отдел'); return; }
    setDepCreateOpen(false); await loadDeps();
  }
  async function updateDep(id, patch) {
    const r = await apiFetch(`${API_URL}/structure/departments/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch)
    });
    if (r.status === 409) { alert('Отдел с таким code уже существует'); return; }
    if (!r.ok) { alert('Не удалось сохранить отдел'); return; }
    setDepEdit(null); await loadDeps();
  }
  async function deleteDep(id) {
    if (!window.confirm('Удалить отдел? У сотрудников отдела связь будет очищена.')) return;
    const r = await apiFetch(`${API_URL}/structure/departments/${id}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось удалить отдел'); return; }
    await loadDeps();
  }

  /* ---------- РАНГИ ---------- */
  const [ranks, setRanks] = useState([]);
  const [ranksLoading, setRanksLoading] = useState(false);
  const [rankMenuOpen, setRankMenuOpen] = useState(null);
  const [rankCreateOpen, setRankCreateOpen] = useState(false);
  const [rankEdit, setRankEdit] = useState(null); // {id, code, name, alt_name, description}

  const loadRanks = useCallback(async () => {
    setRanksLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/structure/ranks`);
      const list = r.ok ? await r.json() : [];
      const arr = Array.isArray(list) ? list.slice() : [];
      // сортируем: сначала по числу из code; если числа нет — по name
      arr.sort((a, b) => {
        const num = (x) => {
          const m = String(x?.code ?? "").match(/\d+/);
          return m ? parseInt(m[0], 10) : null;
        };
        const na = num(a), nb = num(b);
        if (na != null && nb != null) return na - nb;
        return String(a?.name ?? "").localeCompare(String(b?.name ?? ""), "ru", { sensitivity: "base" });
      });
      setRanks(arr); // ✅ сохраняем объекты, не строки
    } finally { setRanksLoading(false); }
  }, []);

  // 🔹 Подгружаем ранги при открытии вкладки «Ранги»
  useEffect(() => {
    if (sub === 'ranks') loadRanks();
  }, [sub, loadRanks]);

  async function createRank(payload) {
    if (!canManageStructure) return;
    const r = await apiFetch(`${API_URL}/structure/ranks`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    if (r.status === 409) { alert('Дубликат code/name'); return; }
    if (!r.ok) { alert('Не удалось создать ранг'); return; }
    setRankCreateOpen(false); await loadRanks();
  }
  async function updateRank(id, patch) {
    if (!canManageStructure) return;
    const r = await apiFetch(`${API_URL}/structure/ranks/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch)
    });
    if (r.status === 409) { alert('Дубликат code/name'); return; }
    if (!r.ok) { alert('Не удалось сохранить ранг'); return; }
    setRankEdit(null); await loadRanks();
  }
  async function deleteRank(id) {
    if (!canManageStructure) return;
    if (!window.confirm('Удалить ранг? У сотрудников связь будет очищена.')) return;
    const r = await apiFetch(`${API_URL}/structure/ranks/${id}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось удалить ранг'); return; }
    await loadRanks();
  }

  /* ---------- ЛИГИ ---------- */
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  const [leagueMenuOpen, setLeagueMenuOpen] = useState(null);
  const [leagueCreateOpen, setLeagueCreateOpen] = useState(false);
  const [leagueEdit, setLeagueEdit] = useState(null); // {id, code, name, alt_name, description}

  const loadLeagues = useCallback(async () => {
    setLeaguesLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/structure/leagues`);
      const list = r.ok ? await r.json() : [];
      const arr = Array.isArray(list) ? list.slice() : [];

      // сортировка как у рангов: сначала число из code, иначе по name
      arr.sort((a, b) => {
        const num = (x) => {
          const m = String(x?.code ?? "").match(/\d+/);
          return m ? parseInt(m[0], 10) : null;
        };
        const na = num(a), nb = num(b);
        if (na != null && nb != null) return na - nb;
        return String(a?.name ?? "").localeCompare(String(b?.name ?? ""), "ru", { sensitivity: "base" });
      });

      setLeagues(arr);
    } finally {
      setLeaguesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sub === 'leagues') loadLeagues();
  }, [sub, loadLeagues]);

  async function createLeague(payload) {
    if (!canManageStructure) return;
    const r = await apiFetch(`${API_URL}/structure/leagues`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    if (r.status === 409) { alert('Дубликат code/name'); return; }
    if (!r.ok) { alert('Не удалось создать лигу'); return; }
    setLeagueCreateOpen(false);
    await loadLeagues();
  }

  async function updateLeague(id, patch) {
    if (!canManageStructure) return;
    const r = await apiFetch(`${API_URL}/structure/leagues/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch)
    });
    if (r.status === 409) { alert('Дубликат code/name'); return; }
    if (!r.ok) { alert('Не удалось сохранить лигу'); return; }
    setLeagueEdit(null);
    await loadLeagues();
  }

  async function deleteLeague(id) {
    if (!canManageStructure) return;
    if (!window.confirm('Удалить лигу? У сотрудников связь будет очищена.')) return;
    const r = await apiFetch(`${API_URL}/structure/leagues/${id}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось удалить лигу'); return; }
    await loadLeagues();
  }

  /* ---------- КОМАНДЫ ---------- */
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamMenuOpen, setTeamMenuOpen] = useState(null);
  const [teamCreateOpen, setTeamCreateOpen] = useState(false);
  const [teamEdit, setTeamEdit] = useState(null); // {id, name, manager_id, description, image_url}

  const loadTeams = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/structure/teams`);
      const list = r.ok ? await r.json() : [];
      const arr = Array.isArray(list) ? list : [];

      // сортировка команд по названию (name)
      const sorted = [...arr].sort((a, b) =>
        String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'ru', { sensitivity: 'base' })
      );
      setTeams(sorted);

      // дополняем кэш имён руководителей команд
      const ids = [...new Set(arr.map(t => t.manager_id).filter(id => typeof id === 'number'))];
      const missing = ids.filter(id => !empNameCache[id]);
      if (missing.length) {
        const pairs = await Promise.all(missing.map(async (id) => {
          const rr = await apiFetch(`${API_URL}/employees/${id}`);
          const emp = rr.ok ? await rr.json() : null;
          return [id, emp?.name || `#${id}`];
        }));
        setEmpNameCache(prev => {
          const next = { ...prev };
          for (const [id, name] of pairs) next[id] = name;
          return next;
        });
      }
    } finally {
      setTeamsLoading(false);
    }
  }, [empNameCache]);

  useEffect(() => { if (sub === 'teams') loadTeams(); }, [sub, loadTeams]);

  async function createTeam(payload, imageFile) {
    // 1) create
    if (!canManageStructure) return;
    const r = await apiFetch(`${API_URL}/structure/teams`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    if (r.status === 409) { alert('Команда с таким названием уже существует'); return; }
    if (!r.ok) { alert('Не удалось создать команду'); return; }
    const team = await r.json();

    // 2) optional image upload
    if (imageFile && team?.id) {
      const fd = new FormData(); fd.append('image', imageFile);
      const up = await apiFetch(`${API_URL}/structure/teams/${team.id}/image`, { method: 'POST', body: fd });
      if (!up.ok) alert('Команда создана, но не удалось загрузить изображение');
    }

    setTeamCreateOpen(false);
    await loadTeams();
  }
  async function updateTeam(id, patch, imageFile) {
    if (!canManageStructure) return;
    const r = await apiFetch(`${API_URL}/structure/teams/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch)
    });
    if (r.status === 409) { alert('Команда с таким названием уже существует'); return; }
    if (!r.ok) { alert('Не удалось сохранить команду'); return; }

    if (imageFile) {
      const fd = new FormData(); fd.append('image', imageFile);
      const up = await apiFetch(`${API_URL}/structure/teams/${id}/image`, { method: 'POST', body: fd });
      if (!up.ok) alert('Данные сохранены, но не удалось загрузить изображение');
    }

    setTeamEdit(null); await loadTeams();
  }
  async function deleteTeam(id) {
    if (!canManageStructure) return;
    if (!window.confirm('Удалить команду? У сотрудников связь будет очищена.')) return;
    const r = await apiFetch(`${API_URL}/structure/teams/${id}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось удалить команду'); return; }
    await loadTeams();
  }

  /* ---------- ВРЕМЕННЫЕ КОМАНДЫ ---------- */
  const [temporaryTeams, setTemporaryTeams] = useState([]);
  const [temporaryTeamsLoading, setTemporaryTeamsLoading] = useState(false);
  const [temporaryTeamMenuOpen, setTemporaryTeamMenuOpen] = useState(null);
  const [temporaryTeamCreateOpen, setTemporaryTeamCreateOpen] = useState(false);
  const [temporaryTeamEdit, setTemporaryTeamEdit] = useState(null); // {id, name, captain_id, description, image_url}

  const loadTemporaryTeams = useCallback(async () => {
    setTemporaryTeamsLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/structure/temporary-teams`);
      const list = r.ok ? await r.json() : [];
      const arr = Array.isArray(list) ? list : [];
      const sorted = [...arr].sort((a, b) =>
        String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'ru', { sensitivity: 'base' })
      );
      setTemporaryTeams(sorted);

      const ids = [...new Set(arr.map(t => t.captain_id).filter(id => typeof id === 'number'))];
      const missing = ids.filter(id => !empNameCache[id]);
      if (missing.length) {
        const pairs = await Promise.all(missing.map(async (id) => {
          const rr = await apiFetch(`${API_URL}/employees/${id}`);
          const emp = rr.ok ? await rr.json() : null;
          return [id, emp?.name || `#${id}`];
        }));
        setEmpNameCache(prev => {
          const next = { ...prev };
          for (const [id, name] of pairs) next[id] = name;
          return next;
        });
      }
    } finally {
      setTemporaryTeamsLoading(false);
    }
  }, [empNameCache]);

  useEffect(() => { if (sub === 'temporaryTeams') loadTemporaryTeams(); }, [sub, loadTemporaryTeams]);

  useEffect(() => {
    const anyMenuOpen = depMenuOpen || rankMenuOpen || leagueMenuOpen || teamMenuOpen || temporaryTeamMenuOpen;
    const onDocClick = (e) => {
      if (!anyMenuOpen) return;
      if (!e.target.closest?.(ADMIN_MENU_OUTSIDE_SELECTOR)) {
        setDepMenuOpen(null);
        setRankMenuOpen(null);
        setLeagueMenuOpen(null);
        setTeamMenuOpen(null);
        setTemporaryTeamMenuOpen(null);
      }
    };
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      setDepMenuOpen(null);
      setRankMenuOpen(null);
      setLeagueMenuOpen(null);
      setTeamMenuOpen(null);
      setTemporaryTeamMenuOpen(null);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [depMenuOpen, rankMenuOpen, leagueMenuOpen, teamMenuOpen, temporaryTeamMenuOpen]);

  async function createTemporaryTeam(payload, imageFile) {
    if (!canManageStructure) return;
    const r = await apiFetch(`${API_URL}/structure/temporary-teams`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    if (r.status === 409) { alert('Временная команда с таким названием уже существует'); return; }
    if (!r.ok) { alert('Не удалось создать временную команду'); return; }
    const team = await r.json();

    if (imageFile && team?.id) {
      const fd = new FormData(); fd.append('image', imageFile);
      const up = await apiFetch(`${API_URL}/structure/temporary-teams/${team.id}/image`, { method: 'POST', body: fd });
      if (!up.ok) alert('Команда создана, но не удалось загрузить изображение');
    }

    setTemporaryTeamCreateOpen(false);
    await loadTemporaryTeams();
  }

  async function updateTemporaryTeam(id, patch, imageFile) {
    if (!canManageStructure) return;
    const hasPatch = patch && Object.keys(patch).length > 0;
    if (hasPatch) {
      const r = await apiFetch(`${API_URL}/structure/temporary-teams/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch)
      });
      if (r.status === 409) { alert('Временная команда с таким названием уже существует'); return; }
      if (!r.ok) { alert('Не удалось сохранить временную команду'); return; }
    }

    if (imageFile) {
      const fd = new FormData(); fd.append('image', imageFile);
      const up = await apiFetch(`${API_URL}/structure/temporary-teams/${id}/image`, { method: 'POST', body: fd });
      if (!up.ok) alert('Не удалось обновить изображение команды');
    }

    setTemporaryTeamEdit(null);
    await loadTemporaryTeams();
  }

  async function deleteTemporaryTeam(id) {
    if (!canManageStructure) return;
    if (!window.confirm('Удалить временную команду? У сотрудников связь будет очищена.')) return;
    const r = await apiFetch(`${API_URL}/structure/temporary-teams/${id}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось удалить временную команду'); return; }
    await loadTemporaryTeams();
  }

  return (
    <div className={s.section}>
      <ScrollTabs items={subTabs} activeKey={sub} onChange={setSub} />

      {/* --- Отделы --- */}
      {sub === 'departments' && (
        <div style={{ paddingTop: 12 }}>
          {depsLoading && <p>Загрузка…</p>}
          <ul className={s.list}>
            {deps.map((d, idx) => (
              <li key={d.id} className={`${s.structureRow} ${depDrag.active && idx === depDrag.overIndex ? s.rowDragging : ''}`}>
                <div
                  className={s.depCode}
                  style={{
                    cursor: canManageStructure ? (depDrag.active ? 'grabbing' : 'grab') : 'default',
                    touchAction: canManageStructure ? 'none' : 'auto',
                  }}
                  title={canManageStructure ? 'Перетащите за аббревиатуру, чтобы изменить порядок' : ''}
                  onPointerDown={canManageStructure ? (ev) => startDepDragFromCode(ev, idx) : undefined}
                  onPointerMove={canManageStructure ? onDepDragMove : undefined}
                  onPointerUp={canManageStructure ? endDepDrag : undefined}
                  onPointerCancel={canManageStructure ? endDepDrag : undefined}
                >
                  {d.code || '—'}
                </div>

                {/* правая колонка: название и ФИО руководителя */}
                <div className={s.depMain}>
                  <div className={s.name}>{d.name}</div>
                  <div className={s.depMgr}>
                    {typeof d.manager_id === 'number'
                      ? (empNameCache[d.manager_id] || `#${d.manager_id}`)
                      : 'Руководитель не указан'}
                  </div>
                </div>

                {canManageStructure && (
                  <AdminRowMenu
                    open={depMenuOpen === d.id}
                    onToggle={(e) => { e.stopPropagation(); setDepMenuOpen(cur => cur === d.id ? null : d.id); }}
                    items={[
                      { key: 'edit', label: 'Редактировать', onClick: () => { setDepMenuOpen(null); setDepEdit(d); } },
                      { key: 'delete', label: 'Удалить', danger: true, onClick: () => { setDepMenuOpen(null); deleteDep(d.id); } },
                    ]}
                  />
                )}
              </li>
            ))}
          </ul>

          {canManageStructure && (
            <button type="button" className={s.fab} aria-label="Добавить отдел" title="Добавить отдел" onClick={() => setDepCreateOpen(true)}>
              <span className={s.fabSvgWrap} aria-hidden="true">
                <FabBubble className={s.fabBubble} />
                <div className={s.fabInner}><AddIconPlus className={s.fabIcon} /></div>
              </span>
            </button>
          )}

          {canManageStructure && depCreateOpen && (
            <DepartmentModal
              title="Новый отдел"
              onCancel={() => setDepCreateOpen(false)}
              onSubmit={createDep}
            />
          )}
          {canManageStructure && depEdit && (
            <DepartmentModal
              title="Редактировать отдел"
              initial={depEdit}
              onCancel={() => setDepEdit(null)}
              onSubmit={(v) => updateDep(depEdit.id, v)}
            />
          )}
        </div>
      )}

      {/* --- Ранги --- */}
      {sub === 'ranks' && (
        <div style={{ paddingTop: 12 }}>
          {ranksLoading && <p>Загрузка…</p>}
          <ul className={s.list}>
            {ranks.map(r => (
              <li key={r.id} className={s.row}>
                {/* левая колонка — аббревиатура */}
                <div className={s.rankCode}>{r.code || '—'}</div>
                {/* правая колонка — name и alt_name */}
                <div className={s.rankMain}>
                  <div className={s.name}>{r.name || 'Без названия'}</div>
                  <div className={s.rankAlt}>
                    {(r.alt_name && r.alt_name.trim()) ? r.alt_name : ''}
                  </div>
                </div>

                {canManageStructure && (
                  <AdminRowMenu
                    open={rankMenuOpen === r.id}
                    onToggle={(e) => { e.stopPropagation(); setRankMenuOpen(cur => cur === r.id ? null : r.id); }}
                    items={[
                      { key: 'edit', label: 'Редактировать', onClick: () => { setRankMenuOpen(null); setRankEdit(r); } },
                      { key: 'delete', label: 'Удалить', danger: true, onClick: () => { setRankMenuOpen(null); deleteRank(r.id); } },
                    ]}
                  />
                )}
              </li>
            ))}
          </ul>
          {canManageStructure && (
            <button type="button" className={s.fab} aria-label="Добавить ранг" title="Добавить ранг" onClick={() => setRankCreateOpen(true)}>
              <span className={s.fabSvgWrap} aria-hidden="true">
                <FabBubble className={s.fabBubble} />
                <div className={s.fabInner}><AddIconPlus className={s.fabIcon} /></div>
              </span>
            </button>
          )}

          {canManageStructure && rankCreateOpen && (
            <RankModal
              title="Новый ранг"
              onCancel={() => setRankCreateOpen(false)}
              onSubmit={createRank}
            />
          )}
          {canManageStructure && rankEdit && (
            <RankModal
              title="Редактировать ранг"
              initial={rankEdit}
              onCancel={() => setRankEdit(null)}
              onSubmit={(v) => updateRank(rankEdit.id, v)}
            />
          )}
        </div>
      )}

      {/* --- Команды --- */}
      {sub === 'teams' && (
        <div style={{ paddingTop: 12 }}>
          {teamsLoading && <p>Загрузка…</p>}
          <ul className={s.list}>
            {teams.map(t => (
              <li key={t.id} className={s.row}>
                <Avatar src={resolveImageUrl(t.image_url)} name={t.name} alt={t.name || ''} size={44} className={s.teamAvatar} />
                <div className={s.main}>
                  <div className={s.name}>{t.name}</div>
                  <div className={s.meta}>
                    {typeof t.manager_id === 'number' ? `${empNameCache[t.manager_id]}` : 'Руководитель не указан'}
                  </div>
                </div>

                {canManageStructure && (
                  <AdminRowMenu
                    open={teamMenuOpen === t.id}
                    onToggle={(e) => { e.stopPropagation(); setTeamMenuOpen(cur => cur === t.id ? null : t.id); }}
                    items={[
                      { key: 'edit', label: 'Редактировать', onClick: () => { setTeamMenuOpen(null); setTeamEdit(t); } },
                      { key: 'delete', label: 'Удалить', danger: true, onClick: () => { setTeamMenuOpen(null); deleteTeam(t.id); } },
                    ]}
                  />
                )}
              </li>
            ))}
          </ul>

          {canManageStructure && (
            <button type="button" className={s.fab} aria-label="Добавить команду" title="Добавить команду" onClick={() => setTeamCreateOpen(true)}>
              <span className={s.fabSvgWrap} aria-hidden="true">
                <FabBubble className={s.fabBubble} />
                <div className={s.fabInner}><AddIconPlus className={s.fabIcon} /></div>
              </span>
            </button>
          )}

          {canManageStructure && teamCreateOpen && (
            <TeamModal
              title="Новая команда"
              onCancel={() => setTeamCreateOpen(false)}
              onSubmit={createTeam}
            />
          )}
          {canManageStructure && teamEdit && (
            <TeamModal
              title="Редактировать команду"
              initial={teamEdit}
              onCancel={() => setTeamEdit(null)}
              onSubmit={(v, file) => updateTeam(teamEdit.id, v, file)}
            />
          )}
        </div>
      )}
      {/* --- Временные команды --- */}
      {sub === 'temporaryTeams' && (
        <div style={{ paddingTop: 12 }}>
          {temporaryTeamsLoading && <p>Загрузка…</p>}
          <ul className={s.list}>
            {temporaryTeams.map(t => (
              <li key={t.id} className={s.row}>
                <Avatar src={resolveImageUrl(t.image_url)} name={t.name} alt={t.name || ''} size={44} className={s.teamAvatar} />
                <div className={s.main}>
                  <div className={s.name}>{t.name}</div>
                  <div className={s.meta}>
                    {typeof t.captain_id === 'number' ? `Ментор: ${empNameCache[t.captain_id] || `#${t.captain_id}`}` : 'Ментор не указан'}
                  </div>
                </div>

                {canManageStructure && (
                  <AdminRowMenu
                    open={temporaryTeamMenuOpen === t.id}
                    onToggle={(e) => { e.stopPropagation(); setTemporaryTeamMenuOpen(cur => cur === t.id ? null : t.id); }}
                    items={[
                      { key: 'edit', label: 'Редактировать', onClick: () => { setTemporaryTeamMenuOpen(null); setTemporaryTeamEdit(t); } },
                      { key: 'delete', label: 'Удалить', danger: true, onClick: () => { setTemporaryTeamMenuOpen(null); deleteTemporaryTeam(t.id); } },
                    ]}
                  />
                )}
              </li>
            ))}
          </ul>

          {canManageStructure && (
            <button type="button" className={s.fab} aria-label="Добавить временную команду" title="Добавить временную команду" onClick={() => setTemporaryTeamCreateOpen(true)}>
              <span className={s.fabSvgWrap} aria-hidden="true">
                <FabBubble className={s.fabBubble} />
                <div className={s.fabInner}><AddIconPlus className={s.fabIcon} /></div>
              </span>
            </button>
          )}

          {canManageStructure && temporaryTeamCreateOpen && (
            <TemporaryTeamModal
              title="Новая временная команда"
              onCancel={() => setTemporaryTeamCreateOpen(false)}
              onSubmit={createTemporaryTeam}
            />
          )}
          {canManageStructure && temporaryTeamEdit && (
            <TemporaryTeamModal
              title="Редактировать временную команду"
              initial={temporaryTeamEdit}
              onCancel={() => setTemporaryTeamEdit(null)}
              onSubmit={(v, file) => updateTemporaryTeam(temporaryTeamEdit.id, v, file)}
            />
          )}
        </div>
      )}
      {/* --- Лиги --- */}
      {sub === 'leagues' && (
        <div style={{ paddingTop: 12 }}>
          {leaguesLoading && <p>Загрузка…</p>}

          <ul className={s.list}>
            {leagues.map(l => (
              <li key={l.id} className={s.row}>
                {/* левая колонка — аббревиатура */}
                <div className={s.rankCode}>{l.code || '—'}</div>

                {/* правая колонка — name и alt_name */}
                <div className={s.rankMain}>
                  <div className={s.name}>{l.name || 'Без названия'}</div>
                  <div className={s.rankAlt}>
                    {(l.alt_name && l.alt_name.trim()) ? l.alt_name : ''}
                  </div>
                </div>

                {canManageStructure && (
                  <AdminRowMenu
                    open={leagueMenuOpen === l.id}
                    onToggle={(e) => { e.stopPropagation(); setLeagueMenuOpen(cur => cur === l.id ? null : l.id); }}
                    items={[
                      { key: 'edit', label: 'Редактировать', onClick: () => { setLeagueMenuOpen(null); setLeagueEdit(l); } },
                      { key: 'delete', label: 'Удалить', danger: true, onClick: () => { setLeagueMenuOpen(null); deleteLeague(l.id); } },
                    ]}
                  />
                )}
              </li>
            ))}
          </ul>

          {canManageStructure && (
            <button
              type="button"
              className={s.fab}
              aria-label="Добавить лигу"
              title="Добавить лигу"
              onClick={() => setLeagueCreateOpen(true)}
            >
              <span className={s.fabSvgWrap} aria-hidden="true">
                <FabBubble className={s.fabBubble} />
                <div className={s.fabInner}><AddIconPlus className={s.fabIcon} /></div>
              </span>
            </button>
          )}

          {canManageStructure && leagueCreateOpen && (
            <LeagueModal
              title="Новая лига"
              onCancel={() => setLeagueCreateOpen(false)}
              onSubmit={createLeague}
            />
          )}

          {canManageStructure && leagueEdit && (
            <LeagueModal
              title="Редактировать лигу"
              initial={leagueEdit}
              onCancel={() => setLeagueEdit(null)}
              onSubmit={(v) => updateLeague(leagueEdit.id, v)}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Модалка Отдела ---------- */
function DepartmentModal({ title, initial, onCancel, onSubmit }) {
  const [code, setCode] = useState(initial?.code || '');
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const mgr = useManagerPicker();

  // локальный резолвер ссылок (нужен для аватаров)
  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) => !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;

  // при открытии — подтягиваем реального руководителя по id
  useEffect(() => {
    (async () => {
      // manager
      if (initial?.manager_id) {
        try {
          const r = await apiFetch(`${API_URL}/employees/${initial.manager_id}`);
          const emp = r.ok ? await r.json() : null;
          mgr.setSelected(
            emp ? { id: emp.id, name: emp.name, avatar: emp.avatar }
              : { id: initial.manager_id, name: `#${initial.manager_id}` }
          );
        } catch {
          mgr.setSelected({ id: initial.manager_id, name: `#${initial.manager_id}` });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = () => {
    const payload = {
      code: String(code || '').trim(),
      name: String(name || '').trim(),
      manager_id: mgr.selected ? Number(mgr.selected.id) : null,
      description: (description || '').trim() || null,
    };
    if (!payload.code || !payload.name) return;
    onSubmit && onSubmit(payload);
  };

  return (
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <div className={s.createForm}>
          <label className={s.label}>Аббревиатура</label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="PR, HR, IT…" ariaLabel="Аббревиатура" />

          <label className={s.label}>Название</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Например, Отдел PR" ariaLabel="Название" />

          <label className={s.label}>Руководитель (сотрудник)</label>
          <ManagerSelector mgr={mgr} resolveImageUrl={resolveImageUrl} />

          <label className={s.label}>Описание</label>
          <Textarea className={s.birthdayText} rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className={s.modalFooter}>
          <button className={s.btnCancel} onClick={onCancel}>Отмена</button>
          <button className={s.btnPrimary} onClick={submit} disabled={!code.trim() || !name.trim()}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Модалка Ранга ---------- */
function RankModal({ title, initial, onCancel, onSubmit }) {
  const [code, setCode] = useState(initial?.code || '');
  const [name, setName] = useState(initial?.name || '');
  const [altName, setAltName] = useState(initial?.alt_name || '');
  const [description, setDescription] = useState(initial?.description || '');

  const submit = () => {
    const payload = {
      code: String(code || '').trim(),
      name: String(name || '').trim(),
      alt_name: (altName || '').trim() || null,
      description: (description || '').trim() || null
    };
    if (!payload.code || !payload.name) return;
    onSubmit && onSubmit(payload);
  };

  return (
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <div className={s.createForm}>
          <label className={s.label}>Аббревиатура</label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="JR, MD, TL…" ariaLabel="Аббревиатура" />

          <label className={s.label}>Название</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Например, Тимлид" ariaLabel="Название" />

          <label className={s.label}>Второе название</label>
          <Input value={altName} onChange={(e) => setAltName(e.target.value)} placeholder="Название для ранговых чатов" ariaLabel="Второе название" />

          <label className={s.label}>Описание</label>
          <Textarea className={s.birthdayText} rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className={s.modalFooter}>
          <button className={s.btnCancel} onClick={onCancel}>Отмена</button>
          <button className={s.btnPrimary} onClick={submit} disabled={!code.trim() || !name.trim()}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Модалка Лиги ---------- */
function LeagueModal({ title, initial, onCancel, onSubmit }) {
  const [code, setCode] = useState(initial?.code || '');
  const [name, setName] = useState(initial?.name || '');
  const [altName, setAltName] = useState(initial?.alt_name || '');
  const [description, setDescription] = useState(initial?.description || '');

  const submit = () => {
    const payload = {
      code: String(code || '').trim(),
      name: String(name || '').trim(),
      alt_name: (altName || '').trim() || null,
      description: (description || '').trim() || null
    };
    if (!payload.code || !payload.name) return;
    onSubmit && onSubmit(payload);
  };

  return (
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <div className={s.createForm}>
          <label className={s.label}>Аббревиатура</label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="A, B, 1…" ariaLabel="Аббревиатура" />

          <label className={s.label}>Название</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Например, Высшая Лига" ariaLabel="Название" />

          <label className={s.label}>Второе название</label>
          <Input value={altName} onChange={(e) => setAltName(e.target.value)} placeholder="Опционально" ariaLabel="Второе название" />

          <label className={s.label}>Описание</label>
          <Textarea className={s.birthdayText} rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className={s.modalFooter}>
          <button className={s.btnCancel} onClick={onCancel}>Отмена</button>
          <button className={s.btnPrimary} onClick={submit} disabled={!code.trim() || !name.trim()}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Модалка Команды (с аплоадом) ---------- */
function TeamModal({ title, initial, onCancel, onSubmit }) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const mgr = useManagerPicker();

  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) => !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;

  useEffect(() => {
    (async () => {
      if (initial?.manager_id) {
        try {
          const r = await apiFetch(`${API_URL}/employees/${initial.manager_id}`);
          const emp = r.ok ? await r.json() : null;
          mgr.setSelected(
            emp ? { id: emp.id, name: emp.name, avatar: emp.avatar }
              : { id: initial.manager_id, name: `#${initial.manager_id}` }
          );
        } catch {
          mgr.setSelected({ id: initial.manager_id, name: `#${initial.manager_id}` });
        }
      }
      if (initial?.image_url) setPreview(resolveImageUrl(initial.image_url));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!file) { setPreview(initial?.image_url ? resolveImageUrl(initial.image_url) : null); return; }
    const url = URL.createObjectURL(file); setPreview(url); return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const submit = () => {
    const payload = {
      name: String(name || '').trim(),
      manager_id: mgr.selected ? Number(mgr.selected.id) : null,
      description: (description || '').trim() || null
    };
    if (!payload.name) return;
    onSubmit && onSubmit(payload, file || null);
  };

  return (
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <div className={s.createForm}>
          <div className={s.teamTopRow}>
            <div className={s.teamImageCol}>
              <label className={s.label}>Изображение</label>
              <div className={s.imageRow}>
                <label
                  title={preview ? 'Сменить изображение' : 'Загрузить изображение'}
                  className={s.imagePicker}
                >
                  <input type="file" accept="image/*" className={s.hiddenInput} onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  {preview ? (
                    <img src={preview} alt="" className={s.imageThumb} />
                  ) : (
                    <div className={s.imageEmptyState}>
                      <UploadChatPictureIcon className={s.uploadIcon} />
                      <span className={s.imageEmptyText}>Нажмите, чтобы загрузить</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div className={s.teamMetaCol}>
              <label className={s.label}>Название</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Например, Команда дизайна" ariaLabel="Название" />

              <label className={s.label}>Руководитель (сотрудник)</label>
              <ManagerSelector mgr={mgr} resolveImageUrl={resolveImageUrl} />
            </div>
          </div>

          <label className={s.label}>Описание</label>
          <Textarea className={s.birthdayText} rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className={s.modalFooter}>
          <button className={s.btnCancel} onClick={onCancel}>Отмена</button>
          <button className={s.btnPrimary} onClick={submit} disabled={!name.trim()}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

function TemporaryTeamModal({ title, initial, onCancel, onSubmit }) {
  const [name, setName] = useState(initial?.name || '');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const captain = useManagerPicker();

  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) => !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;

  useEffect(() => {
    (async () => {
      if (initial?.captain_id) {
        try {
          const r = await apiFetch(`${API_URL}/employees/${initial.captain_id}`);
          const emp = r.ok ? await r.json() : null;
          captain.setSelected(
            emp ? { id: emp.id, name: emp.name, avatar: emp.avatar }
              : { id: initial.captain_id, name: `#${initial.captain_id}` }
          );
        } catch {
          captain.setSelected({ id: initial.captain_id, name: `#${initial.captain_id}` });
        }
      }
      if (initial?.image_url) setPreview(resolveImageUrl(initial.image_url));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!file) { setPreview(initial?.image_url ? resolveImageUrl(initial.image_url) : null); return; }
    const url = URL.createObjectURL(file); setPreview(url); return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const submit = () => {
    const nextName = String(name || '').trim();
    const nextCaptainId = captain.selected ? Number(captain.selected.id) : null;
    if (!nextName) return;

    if (!initial) {
      onSubmit && onSubmit({ name: nextName, captain_id: nextCaptainId }, file || null);
      return;
    }

    const patch = {};
    const prevName = String(initial?.name || '').trim();
    const prevCaptainId = initial?.captain_id == null ? null : Number(initial.captain_id);

    if (nextName !== prevName) patch.name = nextName;
    if (nextCaptainId !== prevCaptainId) patch.captain_id = nextCaptainId;

    onSubmit && onSubmit(patch, file || null);
  };

  return (
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <div className={s.createForm}>
          <div className={s.teamTopRow}>
            <div className={s.teamImageCol}>
              <div className={s.imageRow}>
                <label
                  title={preview ? 'Сменить изображение' : 'Загрузить изображение'}
                  className={s.imagePicker}
                >
                  <input
                    type="file"
                    accept="image/*"
                    className={s.hiddenInput}
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                  {preview ? (
                    <img src={preview} alt="" className={s.imageThumb} />
                  ) : (
                    <div className={s.imageEmptyState}>
                      <UploadChatPictureIcon className={s.uploadIcon} />
                      <span className={s.imageEmptyText}>Нажмите, чтобы загрузить</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div className={s.teamMetaCol}>
              <label className={s.label}>Название <span style={{ color: '#C01048', fontWeight: "400" }}>*</span></label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Введите название команды" ariaLabel="Название" />

              <label className={s.label}>Ментор (сотрудник)</label>
              <ManagerSelector mgr={captain} resolveImageUrl={resolveImageUrl} />

            </div>
          </div>

        </div>

        <div className={s.modalFooter}>
          <button className={s.btnCancel} onClick={onCancel}>Отмена</button>
          <button className={s.btnPrimary} onClick={submit} disabled={!name.trim()}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}

/* ======================= СТРАНИЦА «Параметры» с вложенным таббаром ======================= */

function AdminSettings() {
  const [sub, setSub] = useState('rubrics'); // 'rubrics' | 'series' | 'mainEvent' | 'misc'
  const subTabs = [
    { key: 'rubrics', label: 'Рубрики' },
    { key: 'series', label: 'Серии' },
    { key: 'mainEvent', label: 'Главное событие' }
  ];

  // --- Рубрики ---
  const [rubrics, setRubrics] = useState([]);
  const [rubricsLoading, setRubricsLoading] = useState(false);
  const [rubricCreateOpen, setRubricCreateOpen] = useState(false);
  const [rubricEdit, setRubricEdit] = useState(null); // { id, name, order } | null
  const [rubricMenuOpen, setRubricMenuOpen] = useState(null); // id рубрики или null

  // занятые приоритеты + подсказка свободного
  const occupiedOrders = React.useMemo(
    () => new Set((rubrics || []).map(r => Number(r?.order ?? 0))),
    [rubrics]
  );
  const getNextFreeOrder = React.useCallback((n = 0) => {
    let x = Math.max(0, Number.isFinite(+n) ? +n : 0);
    while (occupiedOrders.has(x)) x++;
    return x;
  }, [occupiedOrders]);

  const loadRubrics = useCallback(async () => {
    setRubricsLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/news/rubrics/all`);
      const list = res.ok ? await res.json() : [];
      setRubrics(Array.isArray(list) ? list : []);
    } finally {
      setRubricsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sub === 'rubrics') loadRubrics();
  }, [sub, loadRubrics]);

  const createRubric = async ({ name, order }) => {
    const ord = Number(order ?? 0);
    if (occupiedOrders.has(ord)) {
      alert(`Приоритет ${ord} уже занят. Свободный: ${getNextFreeOrder(ord)}`);
      return;
    }
    const fd = new FormData();
    fd.append('name', String(name || '').trim());
    fd.append('order', String(ord));
    const r = await apiFetch(`${API_URL}/news/rubrics`, { method: 'POST', body: fd });
    if (r.status === 409) { alert('Рубрика с таким названием/slug уже существует'); return; }
    if (!r.ok) { alert('Не удалось создать рубрику'); return; }
    setRubricCreateOpen(false);
    await loadRubrics();
  };

  const updateRubric = async (id, { name, order }) => {
    const fd = new FormData();
    if (name !== undefined) fd.append('name', String(name).trim());
    if (order !== undefined && order !== null) fd.append('order', String(order));
    const r = await apiFetch(`${API_URL}/news/rubrics/${id}`, { method: 'PATCH', body: fd });
    if (r.status === 409) { alert('Конфликт уникальности (название/slug/приоритет)'); return; }
    if (!r.ok) { alert('Не удалось сохранить рубрику'); return; }
    setRubricEdit(null);
    await loadRubrics();
  };

  const deleteRubric = async (id) => {
    if (!window.confirm('Удалить рубрику? Новости сохранятся без привязки.')) return;
    const r = await apiFetch(`${API_URL}/news/rubrics/${id}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось удалить рубрику'); return; }
    await loadRubrics();
  };

  function RubricModal({ title, initial, onCancel, onSubmit, occupiedOrders = new Set(), suggest }) {
    const [name, setName] = useState(initial?.name ?? '');
    const [order, setOrder] = useState(
      initial?.order === 0 || initial?.order ? String(initial.order) : ''
    );

    const ordNum = order === '' ? 0 : Number(order);
    const initialOrd = Number(initial?.order ?? NaN);
    const isTaken = Number.isFinite(ordNum)
      && occupiedOrders.has(ordNum)
      && !(Number.isFinite(initialOrd) && ordNum === initialOrd);

    const submit = () => {
      const payload = {
        name: (name || '').trim(),
        order: order === '' ? 0 : Number(order),
      };
      if (!payload.name) return;
      if (isTaken) return; // не даём сохранить занятый приоритет
      onSubmit && onSubmit(payload);
    };

    return (
      <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
        <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={s.modalHeader}>
            <strong>{title}</strong>
            <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
          </div>
          <div className={s.createForm}>
            <label className={s.label}>Название рубрики</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="например, Новости отдела"
              ariaLabel="Название рубрики"
            />
            <label className={s.label}>Приоритет</label>
            <Input
              inputMode="numeric"
              value={order}
              onChange={(e) => setOrder(e.target.value.replace(/\D/g, ''))}
              placeholder="0"
              ariaLabel="Приоритет"
            />
            {isTaken && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>
                Такой приоритет уже занят. Свободный:&nbsp;
                <strong>{suggest ? suggest(ordNum) : ''}</strong>{' '}
                <button
                  type="button"
                  className={s.linkBtn}
                  onClick={() => suggest && setOrder(String(suggest(ordNum)))}
                >
                  использовать
                </button>
              </div>
            )}
          </div>
          <div className={s.modalFooter}>
            <button className={s.btnCancel} onClick={onCancel}>Отмена</button>
            <button className={s.btnPrimary} onClick={submit} disabled={!name.trim() || isTaken}>Сохранить</button>
          </div>
        </div>
      </div>
    );
  }

  // --- Серии ---
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [rename, setRename] = useState(null); // { id, name } | null

  // меню действий по троеточию для серии
  const [seriesMenuOpen, setSeriesMenuOpen] = useState(null); // id серии или null

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rubricMenuOpen && !seriesMenuOpen) return;
      if (!e.target.closest?.(ADMIN_MENU_OUTSIDE_SELECTOR)) {
        setRubricMenuOpen(null);
        setSeriesMenuOpen(null);
      }
    };
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      setRubricMenuOpen(null);
      setSeriesMenuOpen(null);
    };

    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [rubricMenuOpen, seriesMenuOpen]);

  const loadSeries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/ratings/series`);
      const list = res.ok ? await res.json() : [];
      setSeries(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sub === 'series') loadSeries();
  }, [sub, loadSeries]);

  const createSeries = async (name) => {
    const r = await apiFetch(`${API_URL}/ratings/series`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    if (r.status === 409) { alert('Серия с таким названием уже существует'); return; }
    if (!r.ok) { alert('Не удалось создать серию'); return; }
    setCreateOpen(false);
    await loadSeries();
  };

  const renameSeries = async (id, name) => {
    const r = await apiFetch(`${API_URL}/ratings/series/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    if (r.status === 409) { alert('Серия с таким названием уже существует'); return; }
    if (!r.ok) { alert('Не удалось переименовать серию'); return; }
    setRename(null);
    await loadSeries();
  };

  const deleteSeries = async (id) => {
    if (!window.confirm('Удалить серию? У сотрудников этой серии место будет очищено.')) return;
    const r = await apiFetch(`${API_URL}/ratings/series/${id}`, { method: 'DELETE' });
    if (!r.ok) { alert('Не удалось удалить серию'); return; }
    await loadSeries();
  };

  const [mainEvent, setMainEvent] = useState({
    is_actual: false,
    title: '',
    description: '',
  });
  const [mainEventLoading, setMainEventLoading] = useState(false);
  const [mainEventSaving, setMainEventSaving] = useState(false);

  const loadMainEvent = useCallback(async () => {
    setMainEventLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/settings/main-event`);
      if (!res.ok) {
        setMainEvent({ is_actual: false, title: '', description: '' });
        return;
      }
      const data = await res.json().catch(() => ({}));
      setMainEvent({
        is_actual: !!data?.is_actual,
        title: String(data?.title || ''),
        description: String(data?.description || ''),
      });
    } finally {
      setMainEventLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sub === 'mainEvent') loadMainEvent();
  }, [sub, loadMainEvent]);

  const saveMainEvent = async () => {
    setMainEventSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/settings/main-event`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_actual: !!mainEvent.is_actual,
          title: String(mainEvent.title || '').trim(),
          description: String(mainEvent.description || '').trim(),
        }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        alert(t || 'Не удалось сохранить главное событие');
        return;
      }
      alert('Главное событие сохранено');
    } finally {
      setMainEventSaving(false);
    }
  };

  return (
    <div className={s.section}>
      <ScrollTabs items={subTabs} activeKey={sub} onChange={setSub} />

      {sub === 'rubrics' && (
        <div style={{ paddingTop: 12 }}>
          {rubricsLoading && <p>Загрузка…</p>}

          <ul className={s.list}>
            {rubrics.map(rb => (
              <li key={rb.id} className={s.row}>
                {/* слева — имя, рядом маленьким приоритет */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <strong style={{ color: '#BD23DB' }}>{rb.name}</strong>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>(приоритет: {rb.order})</span>
                </div>

                <AdminRowMenu
                  open={rubricMenuOpen === rb.id}
                  onToggle={(e) => {
                    e.stopPropagation();
                    setRubricMenuOpen((cur) => (cur === rb.id ? null : rb.id));
                  }}
                  items={[
                    {
                      key: 'edit',
                      label: 'Редактировать',
                      onClick: () => {
                        setRubricMenuOpen(null);
                        setRubricEdit({ id: rb.id, name: rb.name, order: rb.order });
                      },
                    },
                    {
                      key: 'delete',
                      label: 'Удалить',
                      danger: true,
                      onClick: () => {
                        setRubricMenuOpen(null);
                        deleteRubric(rb.id);
                      },
                    },
                  ]}
                />
              </li>
            ))}
          </ul>

          {/* FAB — создать рубрику */}
          <button
            type="button"
            className={s.fab}
            aria-label="Добавить рубрику"
            title="Добавить рубрику"
            onClick={() => setRubricCreateOpen(true)}
          >
            <span className={s.fabSvgWrap} aria-hidden="true">
              <FabBubble className={s.fabBubble} />
              <div className={s.fabInner}><AddIconPlus className={s.fabIcon} /></div>
            </span>
          </button>

          {/* модалка: новая рубрика */}
          {rubricCreateOpen && (
            <RubricModal
              title="Новая рубрика"
              initial={{
                name: '',
                order: rubrics?.length ? Math.max(...rubrics.map(x => x.order ?? 0)) + 1 : 0
              }}
              occupiedOrders={occupiedOrders}
              suggest={getNextFreeOrder}
              onCancel={() => setRubricCreateOpen(false)}
              onSubmit={createRubric}
            />
          )}

          {/* модалка: редактировать рубрику */}
          {rubricEdit && (
            <RubricModal
              title="Редактировать рубрику"
              initial={rubricEdit}
              occupiedOrders={occupiedOrders}
              suggest={getNextFreeOrder}
              onCancel={() => setRubricEdit(null)}
              onSubmit={(v) => updateRubric(rubricEdit.id, v)}
            />
          )}
        </div>
      )}

      {sub === 'series' && (
        <div style={{ paddingTop: 12 }}>
          {loading && <p>Загрузка…</p>}

          <ul className={s.list}>
            {series.map(sr => (
              <li key={sr.id} className={s.row}>
                {/* Оставляем только фиолетовое краткое имя слева */}
                <strong style={{ color: '#BD23DB', width: 44, display: 'inline-block' }}>{sr.name}</strong>

                <AdminRowMenu
                  open={seriesMenuOpen === sr.id}
                  onToggle={(e) => {
                    e.stopPropagation();
                    setSeriesMenuOpen((cur) => (cur === sr.id ? null : sr.id));
                  }}
                  items={[
                    {
                      key: 'edit',
                      label: 'Редактировать',
                      onClick: () => {
                        setSeriesMenuOpen(null);
                        setRename({ id: sr.id, name: sr.name });
                      },
                    },
                    {
                      key: 'delete',
                      label: 'Удалить',
                      danger: true,
                      onClick: () => {
                        setSeriesMenuOpen(null);
                        deleteSeries(sr.id);
                      },
                    },
                  ]}
                />
              </li>
            ))}
          </ul>

          {/* FAB — создать серию */}
          <button
            type="button"
            className={s.fab}
            aria-label="Добавить серию"
            title="Добавить серию"
            onClick={() => setCreateOpen(true)}
          >
            <span className={s.fabSvgWrap} aria-hidden="true">
              <FabBubble className={s.fabBubble} />
              <div className={s.fabInner}><AddIconPlus className={s.fabIcon} /></div>
            </span>
          </button>

          {/* модалка: новая серия */}
          {createOpen && (
            <SeriesNameModal
              title="Новая серия"
              initial=""
              onCancel={() => setCreateOpen(false)}
              onSubmit={createSeries}
            />
          )}

          {/* модалка: переименовать серию */}
          {rename && (
            <SeriesNameModal
              title="Переименовать серию"
              initial={rename.name}
              onCancel={() => setRename(null)}
              onSubmit={(name) => renameSeries(rename.id, name)}
            />
          )}
        </div>
      )}

      {sub === 'misc' && (
        <div style={{ paddingTop: 12 }}>
          <p>Здесь будут дополнительные настройки (???). Пока заглушка.</p>
        </div>
      )}
      {sub === 'mainEvent' && (
        <div className={s.mainEventWrap}>
          {mainEventLoading ? (
            <p>Загрузка…</p>
          ) : (
            <div className={s.mainEventCard}>
              <div className={s.mainEventHeader}>
                <div>
                  <h3 className={s.mainEventTitle}>Главное событие</h3>
                  <p className={s.mainEventSubtitle}>Показывается на главной странице в блоке событий.</p>
                </div>
                <div className={`${s.mainEventStatus} ${mainEvent.is_actual ? s.mainEventStatusActive : ''}`}>
                  {mainEvent.is_actual ? 'Активно' : 'Неактивно'}
                </div>
              </div>

              <div className={s.mainEventForm}>
                <label className={s.label}>Название события</label>
                <Input
                  value={mainEvent.title}
                  onChange={(e) => setMainEvent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Например, Сражения 2026"
                  ariaLabel="Название события"
                />

                <label className={s.label}>Описание</label>
                <Textarea
                  className={`${s.birthdayText} ${s.mainEventTextarea}`}
                  rows={4}
                  value={mainEvent.description}
                  onChange={(e) => setMainEvent(prev => ({ ...prev, description: e.target.value }))}
                />

                <div className={`${s.switchRow} ${s.mainEventSwitchRow}`}>
                  <div className={s.switchText}>
                    <div className={s.switchTitle}>Показывать на главной</div>
                    <div className={s.switchHint}>Включает или выключает актуальность события</div>
                  </div>
                  <label className={s.switch}>
                    <input
                      type="checkbox"
                      checked={!!mainEvent.is_actual}
                      onChange={(e) => setMainEvent(prev => ({ ...prev, is_actual: e.target.checked }))}
                    />
                    <span className={s.slider} />
                  </label>
                </div>
              </div>

              <div className={`${s.actionsRow} ${s.mainEventActions}`}>
                <button type="button" className={s.btnPrimary} onClick={saveMainEvent} disabled={mainEventSaving}>
                  {mainEventSaving ? 'Сохранение…' : 'Сохранить'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AdminNotifications() {
  const [text, setText] = useState('');
  const [isImportant, setIsImportant] = useState(false);

  // фильтры получателей
  const [toDark, setToDark] = useState(false);
  const [toLight, setToLight] = useState(false);
  const [toLeaders, setToLeaders] = useState(false);

  const [sending, setSending] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const send = async () => {
    const t = text.trim();
    setOkMsg('');
    setErrMsg('');

    if (!t) { setErrMsg('Введите текст оповещения'); return; }
    if (!window.confirm('Отправить оповещение выбранной группе?')) return;

    setSending(true);
    try {
      const res = await apiFetch(`${API_URL}/admin/users/notifications/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: t,
          is_important: isImportant,
          to_dark: toDark,
          to_light: toLight,
          to_leaders: toLeaders,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrMsg(data?.detail || 'Ошибка отправки');
        return;
      }

      setOkMsg(`Отправлено: ${data?.sent ?? 'OK'}`);
      setText('');
    } catch {
      setErrMsg('Сеть/сервер недоступны');
    } finally {
      setSending(false);
    }
  };

  const resetFilters = () => {
    setToDark(false);
    setToLight(false);
    setToLeaders(false);
  };

  return (
    <div className={`${s.section} ${s.notificationsLayout}`}>
      <div className={s.notificationsCard}>
        <div className={s.filtersTitle}>Кому отправить</div>

        <div className={s.switchRow}>
          <div className={s.switchText}>
            <div className={s.switchTitle}>Тёмные</div>
          </div>
          <label className={s.switch}>
            <input type="checkbox" checked={toDark} onChange={(e) => setToDark(e.target.checked)} />
            <span className={s.slider} />
          </label>
        </div>

        <div className={s.switchRow}>
          <div className={s.switchText}>
            <div className={s.switchTitle}>Светлые</div>
          </div>
          <label className={s.switch}>
            <input type="checkbox" checked={toLight} onChange={(e) => setToLight(e.target.checked)} />
            <span className={s.slider} />
          </label>
        </div>

        <div className={s.switchRow}>
          <div className={s.switchText}>
            <div className={s.switchTitle}>Руководители</div>
          </div>
          <label className={s.switch}>
            <input type="checkbox" checked={toLeaders} onChange={(e) => setToLeaders(e.target.checked)} />
            <span className={s.slider} />
          </label>
        </div>

        {!!okMsg && <div className={s.okMsg}>{okMsg}</div>}
        {!!errMsg && <div className={s.errMsg}>{errMsg}</div>}
      </div>

      <div className={s.notificationsCard}>
        <div className={s.filtersTitle}>Текст оповещения</div>
        <Textarea
          wrapperClassName={s.fieldTextareaWrap}
          className={s.fieldTextarea}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder="Введите текст…"
        />

        <div className={s.switchRow} style={{ paddingTop: 0 }}>
          <div className={s.switchTitleRow}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11.9998 8.99999V13M11.9998 17H12.0098M10.6151 3.89171L2.39019 18.0983C1.93398 18.8863 1.70588 19.2803 1.73959 19.6037C1.769 19.8857 1.91677 20.142 2.14613 20.3088C2.40908 20.5 2.86435 20.5 3.77487 20.5H20.2246C21.1352 20.5 21.5904 20.5 21.8534 20.3088C22.0827 20.142 22.2305 19.8857 22.2599 19.6037C22.2936 19.2803 22.0655 18.8863 21.6093 18.0983L13.3844 3.89171C12.9299 3.10654 12.7026 2.71396 12.4061 2.58211C12.1474 2.4671 11.8521 2.4671 11.5935 2.58211C11.2969 2.71396 11.0696 3.10655 10.6151 3.89171Z"
                stroke="#C01048"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div className={s.switchText}>
              <div className={s.switchTitle}>Важное</div>
              <div className={s.switchHint}>пометить сообщение как важное</div>
            </div>
          </div>
          <label className={s.switch}>
            <input type="checkbox" checked={isImportant} onChange={(e) => setIsImportant(e.target.checked)} />
            <span className={s.slider} />
          </label>
        </div>
      </div>

      <div className={s.actionsRow}>
        <button type="button" className={s.ghostBtn} onClick={resetFilters} disabled={sending}>
          Сбросить
        </button>
        <button type="button" className={s.primaryBtn} onClick={send} disabled={sending}>
          {sending ? 'Отправка…' : 'Отправить'}
        </button>
      </div>
    </div>
  );
}


function EmployeeFormSections({
  isPersonalOpen,
  setIsPersonalOpen,
  isWorkOpen,
  setIsWorkOpen,
  isStructureOpen,
  setIsStructureOpen,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  birthDate,
  setBirthDate,
  gender,
  setGender,
  birthPlace,
  setBirthPlace,
  aboutMe,
  setAboutMe,
  departmentId,
  setDepartmentId,
  deps,
  position,
  setPosition,
  teamId,
  setTeamId,
  teams,
  temporaryTeamId,
  setTemporaryTeamId,
  temporaryTeams,
  roleId,
  setRoleId,
  roles,
  rankId,
  setRankId,
  ranks,
  leagueId,
  setLeagueId,
  leagues,
  hireDate,
  setHireDate,
  mgr,
  resolveImageUrl,
  responsibilityDepIds,
  setResponsibilityDepIds,
  connections,
  side,
  setSide,
  showAboutMe = false,
}) {
  return (
    <>
      <button type="button" className={s.fieldHeaderSection} onClick={() => setIsPersonalOpen((v) => !v)}>
        <span>Личные данные</span>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isPersonalOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .2s ease' }}>
          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isPersonalOpen && (
        <>
          <div className={s.createGridRow}>
            <div>
              <div className={s.fieldHeader}>
                <label className={s.label}>Имя <span style={{ color: '#C01048', fontWeight: "400" }}>*</span></label>
              </div>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} ariaLabel="Имя" />
            </div>
            <div>
              <div className={s.fieldHeader}>
                <label className={s.label}>Фамилия <span style={{ color: '#C01048', fontWeight: "400" }}>*</span></label>
              </div>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} ariaLabel="Фамилия" />
            </div>
          </div>
          <div>
            <div className={s.fieldHeader}>
              <label className={s.label}>Email <span style={{ color: '#C01048', fontWeight: "400" }}>*</span></label>
            </div>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} ariaLabel="Email" />
          </div>
          <div className={s.createGridRow}>
            <div>
              <label className={s.label}>Дата рождения</label>
              <DateInput value={birthDate} onChange={(e) => setBirthDate(e.target.value)} ariaLabel="Дата рождения" />
            </div>
            <div>
              <label className={s.label}>Пол</label>
              <div className={s.pillRow}>
                <button type="button" onClick={() => setGender('male')} className={`${s.pill} ${gender === 'male' ? s.pillActive : ''}`}>Мужской</button>
                <button type="button" onClick={() => setGender('female')} className={`${s.pill} ${gender === 'female' ? s.pillActive : ''}`}>Женский</button>
              </div>
            </div>
          </div>
          <div>
            <label className={s.label}>Место рождения</label>
            <Input value={birthPlace} onChange={(e) => setBirthPlace(e.target.value)} ariaLabel="Место рождения" />
          </div>
          {showAboutMe && (
            <div>
              <label className={s.label}>О себе</label>
              <Textarea className={s.birthdayText} rows={4} maxLength={500} value={aboutMe} onChange={(e) => setAboutMe(e.target.value)} placeholder="Напишите о себе" ariaLabel="О себе" />
              <div className={s.meta} style={{ textAlign: 'right', marginTop: 4 }}>
                {(aboutMe || '').length}/500
              </div>
            </div>
          )}
        </>
      )}

      <button type="button" className={s.fieldHeaderSection} onClick={() => setIsWorkOpen((v) => !v)}>
        <span>Рабочие данные</span>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isWorkOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .2s ease' }}>
          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isWorkOpen && (
        <>
          <div className={s.createGridRow}>
            <div>
              <div className={s.fieldHeader}>
                <label className={s.label}>Отдел <span style={{ color: '#C01048', fontWeight: "400" }}>*</span></label>
              </div>
              <Select className={s.fieldInput} value={departmentId ?? ''} onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : null)} ariaLabel="Отдел">
                <option value="">—</option>
                {deps.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </div>
            <div>
              <div className={s.fieldHeader}>
                <label className={s.label}>Должность <span style={{ color: '#C01048', fontWeight: "400" }}>*</span></label>
              </div>
              <Input value={position} onChange={(e) => setPosition(e.target.value)} ariaLabel="Должность" />
            </div>
          </div>
          <div className={s.createGridRow}>
            <div>
              <label className={s.label}>Команда</label>
              <Select className={s.fieldInput} value={teamId ?? ''} onChange={(e) => setTeamId(e.target.value ? Number(e.target.value) : null)} ariaLabel="Команда">
                <option value="">—</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </div>
            <div>
              <label className={s.label}>Роль</label>
              <Select className={s.fieldInput} value={roleId ?? ''} onChange={(e) => setRoleId(e.target.value ? Number(e.target.value) : null)} ariaLabel="Роль">
                <option value="">—</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
            </div>
          </div>
          <div className={s.createGridRow}>
            <div>
              <label className={s.label}>Ранг</label>
              <Select className={s.fieldInput} value={rankId ?? ''} onChange={(e) => setRankId(e.target.value ? Number(e.target.value) : null)} ariaLabel="Ранг">
                <option value="">—</option>
                {ranks.map(r => <option key={r.id} value={r.id}>{r.name}{r.alt_name ? ` — ${r.alt_name}` : ''}</option>)}
              </Select>
            </div>
            <div>
              <label className={s.label}>Лига</label>
              <Select className={s.fieldInput} value={leagueId ?? ''} onChange={(e) => setLeagueId(e.target.value ? Number(e.target.value) : null)} ariaLabel="Лига">
                <option value="">—</option>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name}{l.code ? ` (${l.code})` : ''}</option>)}
              </Select>
            </div>
          </div>
          <div>
            <label className={s.label}>Дата трудоустройства</label>
            <DateInput value={hireDate} onChange={(e) => setHireDate(e.target.value)} ariaLabel="Дата трудоустройства" />
          </div>
        </>
      )}

      <button type="button" className={s.fieldHeaderSection} onClick={() => setIsStructureOpen((v) => !v)}>
        <span>Структура</span>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: isStructureOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .2s ease' }}>
          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isStructureOpen && (
        <>
          <div>
            <label className={s.label}>Руководитель</label>
            <ManagerSelector mgr={mgr} resolveImageUrl={resolveImageUrl} />
          </div>
          <div>
            <label className={s.label}>Зоны ответственности</label>
            <DepartmentMultiSelect deps={deps} selectedIds={responsibilityDepIds} setSelectedIds={setResponsibilityDepIds} />
          </div>
          <div>
            <label className={s.label}>Связи</label>
            <MultiEmployeeSelector picker={connections} resolveImageUrl={resolveImageUrl} />
          </div>
          <label className={s.label}>Сторона</label>
          <div className={s.pillRow}>
            <button type="button" onClick={() => setSide('light')} className={`${s.pill} ${side === 'light' ? s.pillActive : ''}`}>Светлый</button>
            <button type="button" onClick={() => setSide('dark')} className={`${s.pill} ${side === 'dark' ? s.pillActive : ''}`}>Тёмный</button>
          </div>
          <div>
            <label className={s.label}>Временная команда</label>
            <Select className={s.fieldInput} value={temporaryTeamId ?? ''} onChange={(e) => setTemporaryTeamId(e.target.value ? Number(e.target.value) : null)} ariaLabel="Временная команда">
              <option value="">—</option>
              {temporaryTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </div>
        </>
      )}
    </>
  );
}

/* ======================= МОДАЛКА СОЗДАНИЯ ======================= */

function CreateUserEmployeeModal({ onClose, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRankId, setSelectedRankId] = useState(null);
  const [gender, setGender] = useState('');
  const [side, setSide] = useState(null); // 'light' | 'dark' | null
  const [birthDate, setBirthDate] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [departmentId, setDepartmentId] = useState(null);
  const [responsibilityDepIds, setResponsibilityDepIds] = useState([]); // int[]
  const [position, setPosition] = useState('');
  const mgr = useManagerPicker();
  const connections = useMultiEmployeePicker();
  const [teamId, setTeamId] = useState(null);
  const [temporaryTeamId, setTemporaryTeamId] = useState(null);
  const [hireDate, setHireDate] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isPersonalOpen, setIsPersonalOpen] = useState(true);
  const [isWorkOpen, setIsWorkOpen] = useState(true);
  const [isStructureOpen, setIsStructureOpen] = useState(true);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // словари структуры
  const [deps, setDeps] = useState([]);
  const [teams, setTeams] = useState([]);
  const [temporaryTeams, setTemporaryTeams] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState(null);

  useEffect(() => {
    if (!avatarFile) { setAvatarPreview(null); return; }
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  // локальный резолвер ссылок для аватаров в дропдауне
  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) =>
    !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;
  // загрузка словарей отделов / рангов / команд
  useEffect(() => {
    (async () => {
      try {
        const [dRes, rRes, lRes, tRes, ttRes, rolesRes] = await Promise.all([
          apiFetch(`${API_URL}/structure/departments`),
          apiFetch(`${API_URL}/structure/ranks`),
          apiFetch(`${API_URL}/structure/leagues`),
          apiFetch(`${API_URL}/structure/teams`),
          apiFetch(`${API_URL}/structure/temporary-teams`),
          apiFetch(`${API_URL}/admin/users/roles`),
        ]);

        setDeps(dRes.ok ? await dRes.json() : []);
        setRanks(rRes.ok ? await rRes.json() : []);

        const leaguesData = lRes.ok ? await lRes.json() : [];
        setLeagues(sortLeaguesByCode(leaguesData));

        setTeams(tRes.ok ? await tRes.json() : []);
        setTemporaryTeams(ttRes.ok ? await ttRes.json() : []);

        const rolesData = rolesRes.ok ? await rolesRes.json() : [];
        const normalizedRoles = Array.isArray(rolesData) ? rolesData : [];
        setRoles(normalizedRoles);
        setRoleId((prev) => prev ?? getDefaultRoleId(normalizedRoles));
      } catch {
        setDeps([]);
        setRanks([]);
        setLeagues([]);
        setTeams([]);
        setTemporaryTeams([]);
        setRoles([]);
        setRoleId(null);
      }
    })();
  }, []);


  const create = async (e) => {
    e.preventDefault();
    setErr('');
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setErr('Заполните имя, фамилию и email'); return;
    }
    if (departmentId === null || departmentId === undefined) {
      setErr('Выберите отдел'); return;
    }
    if (!position.trim()) {
      setErr('Заполните поле «Должность»'); return;
    }
    setBusy(true);
    try {
      // 0) пробуем найти уже существующего пользователя по email
      const existsRes = await apiFetch(
        `${API_URL}/admin/users?email=${encodeURIComponent(email.trim())}`
      );
      let user = null;
      if (existsRes.ok) {
        const list = await existsRes.json().catch(() => []);
        if (Array.isArray(list)) {
          user = list.find(u => String(u.email).toLowerCase() === email.trim().toLowerCase()) || null;
        }
      }
      // 1) создаём пользователя только если не найден
      if (!user) {
        const uRes = await apiFetch(`${API_URL}/admin/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`,
            role_id: roleId ?? getDefaultRoleId(roles),
            is_active: true
          }),
        });
        if (!uRes.ok) {
          const t = await uRes.text().catch(() => '');
          throw new Error(t || 'Не удалось создать пользователя');
        }
        user = await uRes.json();
      }

      // 2) создаём сотрудника
      const empRes = await apiFetch(`${API_URL}/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${firstName.trim()} ${lastName.trim()}`,
          position: position.trim(),
          email: email.trim(),
          birth_date: birthDate || null,
          birth_place: birthPlace || null,
          manager_id: mgr.selected ? Number(mgr.selected.id) : null,
          connections: (connections.selected || [])
            .map(x => Number(x.id))
            .filter(id => Number.isFinite(id) && id > 0),
          responsibility_departments: (responsibilityDepIds || [])
            .map(Number)
            .filter(x => Number.isFinite(x) && x > 0),
          gender,
          hire_date: hireDate || null,
          department_id: departmentId,           // FK вместо строки
          team_id: teamId ?? null,               // FK вместо строки
          temporary_team_id: temporaryTeamId ?? null,
          league_id: leagueId ?? null,
          side: side || null,
        }),
      });
      if (!empRes.ok) {
        const t = await empRes.text().catch(() => '');
        throw new Error(t || 'Не удалось создать сотрудника');
      }
      const employee = await empRes.json();

      // 2б) если выбран ранг — отдельный вызов смены ранга
      if (selectedRankId && employee?.id) {
        const rnk = await apiFetch(`${API_URL}/employees/${employee.id}/rank`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rank_id: selectedRankId }),
        });
        if (!rnk.ok) {
          const t = await rnk.text().catch(() => '');
          throw new Error(t || 'Не удалось установить ранг');
        }
      }
      if (!empRes.ok) {
        const t = await empRes.text().catch(() => '');
        throw new Error(t || 'Не удалось создать сотрудника');
      }

      // 3) аватар
      if (avatarFile && employee?.id) {
        const fd = new FormData();
        fd.append('avatar', avatarFile);
        const avRes = await apiFetch(`${API_URL}/employees/${employee.id}/avatar`, {
          method: 'POST',
          body: fd,
        });
        if (!avRes.ok) {
          const t = await avRes.text().catch(() => '');
          throw new Error(t || 'Не удалось загрузить аватар');
        }
      }

      // 4) привязка user ↔ employee (user гарантирован после шага 0/1)
      const linkRes = await apiFetch(`${API_URL}/admin/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: employee.id }),
      });
      if (!linkRes.ok) {
        const t = await linkRes.text().catch(() => '');
        throw new Error(t || 'Не удалось привязать сотрудника к пользователю');
      }

      onCreated?.();
    } catch (e2) {
      setErr(e2?.message || 'Ошибка создания');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={s.modalBackdrop}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>Новый сотрудник</strong>
          <button onClick={onClose} className={s.modalClose} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <form id="createForm" onSubmit={create} className={s.createForm}>
          <div className={s.createGrid}>
            <UploadImage
              onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
              previewUrl={avatarPreview}
              accept="image/*"
              title="Загрузить аватар"
              ariaLabel="Загрузить аватар"
            />

            <EmployeeFormSections
              isPersonalOpen={isPersonalOpen}
              setIsPersonalOpen={setIsPersonalOpen}
              isWorkOpen={isWorkOpen}
              setIsWorkOpen={setIsWorkOpen}
              isStructureOpen={isStructureOpen}
              setIsStructureOpen={setIsStructureOpen}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              email={email}
              setEmail={setEmail}
              birthDate={birthDate}
              setBirthDate={setBirthDate}
              gender={gender}
              setGender={setGender}
              birthPlace={birthPlace}
              setBirthPlace={setBirthPlace}
              departmentId={departmentId}
              setDepartmentId={setDepartmentId}
              deps={deps}
              position={position}
              setPosition={setPosition}
              teamId={teamId}
              setTeamId={setTeamId}
              teams={teams}
              temporaryTeamId={temporaryTeamId}
              setTemporaryTeamId={setTemporaryTeamId}
              temporaryTeams={temporaryTeams}
              roleId={roleId}
              setRoleId={setRoleId}
              roles={roles}
              rankId={selectedRankId}
              setRankId={setSelectedRankId}
              ranks={ranks}
              leagueId={leagueId}
              setLeagueId={setLeagueId}
              leagues={leagues}
              hireDate={hireDate}
              setHireDate={setHireDate}
              mgr={mgr}
              resolveImageUrl={resolveImageUrl}
              responsibilityDepIds={responsibilityDepIds}
              setResponsibilityDepIds={setResponsibilityDepIds}
              connections={connections}
              side={side}
              setSide={setSide}
            />


          </div>

          {err && <div style={{ color: '#b91c1c', marginTop: 8, fontSize: 13 }}>{err}</div>}
        </form>

        <div className={s.modalFooter}>
          <button type="button" className={s.btnCancel} onClick={onClose}>Отменить</button>
          <button
            type="submit"
            form="createForm"
            className={s.btnPrimary}
            disabled={busy || !firstName.trim() || !lastName.trim() || !email.trim() || departmentId == null || !position.trim()}
          >
            Создать
          </button>

        </div>
      </div>
    </div>
  );
}
/* ======================= МОДАЛКА СЕРИИ ======================= */
function SeriesNameModal({ title, initial, onCancel, onSubmit }) {
  const [name, setName] = useState(initial || '');
  return (
    <div className={s.modalBackdrop} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>{title}</strong>
          <button className={s.modalClose} onClick={onCancel} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>
        <div className={s.createForm}>
          <label className={s.label}>Название серии</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="например, F1"
            ariaLabel="Название серии"
          />
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnCancel} onClick={onCancel}>Отмена</button>
          <button className={s.btnPrimary} onClick={() => onSubmit(name.trim())} disabled={!name.trim()}>
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}


/* ======================= МОДАЛКА РЕДАКТИРОВАНИЯ ======================= */

function EditUserEmployeeModal({ onClose, onSaved, ranks, employee, user, allEmployees }) {
  const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
  const resolveImageUrl = (path) =>
    !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;

  // разложим имя на 2 части
  const splitName = (full = '') => {
    const parts = (full || '').trim().split(/\s+/);
    if (parts.length <= 1) return { first: full || '', last: '' };
    return { first: parts.slice(0, -1).join(' '), last: parts.slice(-1).join(' ') };
  };
  const n = splitName(employee?.name || '');

  const [firstName, setFirstName] = useState(n.first);
  const [lastName, setLastName] = useState(n.last);
  const [email, setEmail] = useState(user?.email || '');
  const [roleId, setRoleId] = useState(typeof user?.role_id === 'number' ? user.role_id : null);
  const [roles, setRoles] = useState([]);
  const [rankId, setRankId] = useState(typeof employee?.rank_id === 'number' ? employee.rank_id : null);
  const [leagueId, setLeagueId] = useState(typeof employee?.league_id === 'number' ? employee.league_id : null);
  const [ranksDict, setRanksDict] = useState([]); // <-- подняли объявление выше

  // когда подтянули словарь рангов, если rank_id пуст, но есть строковый rank — попробуем сопоставить
  const rankInitializedRef = useRef(false);

  useEffect(() => {
    if (rankInitializedRef.current) return;
    if (rankId != null) {
      rankInitializedRef.current = true;
      return;
    }
    if (!(employee?.rank ?? '').trim()) {
      rankInitializedRef.current = true;
      return;
    }
    if (!Array.isArray(ranksDict) || !ranksDict.length) return;

    const found = ranksDict.find(r => String(r.name) === String(employee.rank));
    if (found) setRankId(found.id);

    rankInitializedRef.current = true;
  }, [ranksDict, employee, rankId]);

  const [gender, setGender] = useState(employee?.gender ?? '');
  const [birthDate, setBirthDate] = useState(employee?.birth_date || '');
  const [birthPlace, setBirthPlace] = useState(employee?.birth_place || '');
  const [aboutMe, setAboutMe] = useState(employee?.about_me || '');
  const [departmentId, setDepartmentId] = useState(
    typeof employee?.department_id === 'number' ? employee.department_id : null
  );
  const [position, setPosition] = useState(employee?.position || '');
  const [teamId, setTeamId] = useState(
    typeof employee?.team_id === 'number' ? employee.team_id : null
  );
  const [temporaryTeamId, setTemporaryTeamId] = useState(
    typeof employee?.temporary_team_id === 'number' ? employee.temporary_team_id : null
  );
  // сторона (light|dark|null)
  const [side, setSide] = useState(employee?.side ?? null);
  // словари
  const [deps, setDeps] = useState([]);
  const [teams, setTeams] = useState([]);
  const [temporaryTeams, setTemporaryTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [hireDate, setHireDate] = useState(employee?.hire_date || '');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isPersonalOpen, setIsPersonalOpen] = useState(true);
  const [isWorkOpen, setIsWorkOpen] = useState(true);
  const [isStructureOpen, setIsStructureOpen] = useState(true);

  // фиксируем исходный ранг, чтобы знать, менялся ли он
  const initialRankRef = useRef(employee?.rank ?? '');

  // выбор руководителя через общий хук
  const mgr = useManagerPicker();
  useEffect(() => {
    (async () => {
      try {
        const [dRes, rRes, lRes, tRes, ttRes, rolesRes] = await Promise.all([
          apiFetch(`${API_URL}/structure/departments`),
          apiFetch(`${API_URL}/structure/ranks`),
          apiFetch(`${API_URL}/structure/leagues`),
          apiFetch(`${API_URL}/structure/teams`),
          apiFetch(`${API_URL}/structure/temporary-teams`),
          apiFetch(`${API_URL}/admin/users/roles`),
        ]);

        setDeps(dRes.ok ? await dRes.json() : []);
        setRanksDict(rRes.ok ? await rRes.json() : []);

        const leaguesData = lRes.ok ? await lRes.json() : [];
        setLeagues(sortLeaguesByCode(leaguesData));

        setTeams(tRes.ok ? await tRes.json() : []);
        setTemporaryTeams(ttRes.ok ? await ttRes.json() : []);

        const rolesData = rolesRes.ok ? await rolesRes.json() : [];
        const normalizedRoles = Array.isArray(rolesData) ? rolesData : [];
        setRoles(normalizedRoles);
        setRoleId((prev) => prev ?? getDefaultRoleId(normalizedRoles));
      } catch {
        setDeps([]);
        setRanksDict([]);
        setLeagues([]);
        setTeams([]);
        setTemporaryTeams([]);
        setRoles([]);
        setRoleId(null);
      }
    })();
  }, []);
  // установить выбранного руководителя по initial данным
  useEffect(() => {
    (async () => {
      const mid = employee?.manager_id;
      if (!mid) return;

      const fromList =
        (allEmployees || []).find(e => String(e.id) === String(mid)) || null;

      if (fromList) {
        mgr.setSelected(fromList);
        return;
      }

      // если руководителя нет в текущем items (например, после поиска) — тянем с бэка
      try {
        const r = await apiFetch(`${API_URL}/employees/${mid}`);
        const emp = r.ok ? await r.json() : null;
        mgr.setSelected(
          emp ? { id: emp.id, name: emp.name, avatar: emp.avatar }
            : { id: mid, name: `#${mid}` }
        );
      } catch {
        mgr.setSelected({ id: mid, name: `#${mid}` });
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connections = useMultiEmployeePicker();
  useEffect(() => {
    (async () => {
      const id = employee?.id;
      if (!id) { connections.setSelected([]); return; }

      // пытаемся взять из employee.connections, если уже есть
      let ids = Array.isArray(employee?.connections) ? employee.connections : null;

      // если нет — догружаем с бэка
      if (!ids) {
        try {
          const r = await apiFetch(`${API_URL}/employees/${id}`);
          const empFull = r.ok ? await r.json() : null;
          ids = Array.isArray(empFull?.connections) ? empFull.connections : [];
        } catch {
          ids = [];
        }
      }

      const uniq = [...new Set((ids || []).map(Number).filter(x => Number.isFinite(x) && x > 0))];

      // соберём объекты сотрудников для UI (из allEmployees или через /employees/{id})
      const items = await Promise.all(uniq.map(async (cid) => {
        const fromList = (allEmployees || []).find(e => String(e.id) === String(cid));
        if (fromList) return fromList;
        try {
          const r = await apiFetch(`${API_URL}/employees/${cid}`);
          const emp = r.ok ? await r.json() : null;
          return emp ? { id: emp.id, name: emp.name, avatar: emp.avatar } : { id: cid, name: `#${cid}` };
        } catch {
          return { id: cid, name: `#${cid}` };
        }
      }));

      connections.setSelected(items);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [responsibilityDepIds, setResponsibilityDepIds] = useState(
    Array.isArray(employee?.responsibility_departments)
      ? employee.responsibility_departments.map(Number).filter(x => Number.isFinite(x) && x > 0)
      : []
  );

  // если в employee нет responsibility_departments — догружаем полную карточку
  useEffect(() => {
    (async () => {
      const id = employee?.id;
      if (!id) return;

      if (Array.isArray(employee?.responsibility_departments)) return;

      try {
        const r = await apiFetch(`${API_URL}/employees/${id}`);
        const empFull = r.ok ? await r.json() : null;
        const ids = Array.isArray(empFull?.responsibility_departments) ? empFull.responsibility_departments : [];
        setResponsibilityDepIds(
          ids.map(Number).filter(x => Number.isFinite(x) && x > 0)
        );
      } catch {
        setResponsibilityDepIds([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!avatarFile) { setAvatarPreview(null); return; }
    const fr = new FileReader();
    fr.onload = () => setAvatarPreview(fr.result);
    fr.readAsDataURL(avatarFile);
  }, [avatarFile]);

  const save = async (e) => {
    e.preventDefault();
    setErr('');
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setErr('Заполните имя, фамилию и email'); return;
    }
    if (departmentId === null || departmentId === undefined) {
      setErr('Выберите отдел'); return;
    }
    if (!position.trim()) {
      setErr('Заполните поле «Должность»'); return;
    }

    setBusy(true);
    try {
      const id = employee.id;

      // 1) PATCH сотрудника (без rank!)
      const empPayload = {
        name: `${firstName.trim()} ${lastName.trim()}`,
        position: (position || '').trim(),
        email: email.trim(),
        birth_date: birthDate || null,
        birth_place: (birthPlace || '').trim() || null,
        manager_id: mgr.selected ? Number(mgr.selected.id) : null,
        connections: (connections.selected || [])
          .map(x => Number(x.id))
          .filter(id => Number.isFinite(id) && id > 0),
        responsibility_departments: (responsibilityDepIds || [])
          .map(Number)
          .filter(x => Number.isFinite(x) && x > 0),
        gender,
        hire_date: hireDate || null,
        department_id: departmentId,
        team_id: teamId ?? null,
        temporary_team_id: temporaryTeamId ?? null,
        league_id: leagueId ?? null,
        about_me: (aboutMe || '').trim() || null,
        side: side || null,
      };

      const empRes = await apiFetch(`${API_URL}/employees/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(empPayload),
      });
      if (!empRes.ok) {
        const t = await empRes.text().catch(() => '');
        throw new Error(t || 'Не удалось сохранить изменения сотрудника');
      }

      // 2) роль (если поменяли) — общий PATCH /admin/users/{id}
      if (user?.id && (roleId ?? null) !== (user?.role_id ?? null)) {
        const rRes = await apiFetch(`${API_URL}/admin/users/${user.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role_id: roleId }),
        });
        if (!rRes.ok) {
          const t = await rRes.text().catch(() => '');
          throw new Error(t || 'Не удалось изменить роль пользователя');
        }
      }

      // 3) email пользователя (если поменяли)
      if (user?.id && String(email) !== String(user.email)) {
        const uRes = await apiFetch(`${API_URL}/admin/users/${user.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (!uRes.ok) {
          const t = await uRes.text().catch(() => '');
          throw new Error(t || 'Не удалось изменить email пользователя');
        }
      }

      // 4) смена ранга (отдельный эндпойнт, только если изменился)
      const initialRankId = typeof employee?.rank_id === 'number' ? employee.rank_id : null;
      const initialRankName = String(initialRankRef.current || '');
      const currentRankName = (() => {
        const r = (ranksDict || []).find(x => x.id === rankId);
        return r ? r.name : '';
      })();
      const changed = (rankId ?? null) !== (initialRankId ?? null) || currentRankName !== initialRankName;

      if (changed) {
        const rnk = await apiFetch(`${API_URL}/employees/${id}/rank`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rank_id: rankId }), // бэкенд сам синхронизирует name + id
        });

        if (!rnk.ok) {
          const t = await rnk.text().catch(() => '');
          throw new Error(t || 'Не удалось изменить ранг');
        }
      }

      // 5) загрузка нового аватара (если выбран)
      if (avatarFile) {
        const fd = new FormData();
        fd.append('avatar', avatarFile);
        const up = await apiFetch(`${API_URL}/employees/${id}/avatar`, { method: 'POST', body: fd });
        if (!up.ok) {
          const t = await up.text().catch(() => '');
          throw new Error(t || 'Не удалось загрузить аватар');
        }
      }

      onSaved && onSaved();
    } catch (e2) {
      setErr(e2?.message || 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={s.modalBackdrop}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>Редактировать</strong>
          <button onClick={onClose} className={s.modalClose} aria-label="Закрыть"><CloseIcon className={s.closeIcon} /></button>
        </div>

        <form id="editForm" onSubmit={save} className={s.createForm}>
          <div className={s.createGrid}>
            <UploadImage
              onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
              previewUrl={avatarPreview || (employee?.avatar ? resolveImageUrl(employee.avatar) : null)}
              accept="image/*"
              title="Загрузить аватар"
              ariaLabel="Загрузить аватар"
            />
            <EmployeeFormSections
              isPersonalOpen={isPersonalOpen}
              setIsPersonalOpen={setIsPersonalOpen}
              isWorkOpen={isWorkOpen}
              setIsWorkOpen={setIsWorkOpen}
              isStructureOpen={isStructureOpen}
              setIsStructureOpen={setIsStructureOpen}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              email={email}
              setEmail={setEmail}
              birthDate={birthDate}
              setBirthDate={setBirthDate}
              gender={gender}
              setGender={setGender}
              birthPlace={birthPlace}
              setBirthPlace={setBirthPlace}
              aboutMe={aboutMe}
              setAboutMe={setAboutMe}
              departmentId={departmentId}
              setDepartmentId={setDepartmentId}
              deps={deps}
              position={position}
              setPosition={setPosition}
              teamId={teamId}
              setTeamId={setTeamId}
              teams={teams}
              temporaryTeamId={temporaryTeamId}
              setTemporaryTeamId={setTemporaryTeamId}
              temporaryTeams={temporaryTeams}
              roleId={roleId}
              setRoleId={setRoleId}
              roles={roles}
              rankId={rankId}
              setRankId={setRankId}
              ranks={ranksDict}
              leagueId={leagueId}
              setLeagueId={setLeagueId}
              leagues={leagues}
              hireDate={hireDate}
              setHireDate={setHireDate}
              mgr={mgr}
              resolveImageUrl={resolveImageUrl}
              responsibilityDepIds={responsibilityDepIds}
              setResponsibilityDepIds={setResponsibilityDepIds}
              connections={connections}
              side={side}
              setSide={setSide}
              showAboutMe
            />

          </div>

          {err && <div style={{ color: '#b91c1c', marginTop: 8, fontSize: 13 }}>{err}</div>}
        </form>

        <div className={s.modalFooter}>
          <button type="button" className={s.btnCancel} onClick={onClose}>Отменить</button>
          <button type="submit" form="editForm" className={s.btnPrimary} disabled={busy}>Сохранить</button>
        </div>
      </div>
    </div>
  );
}
